import { Component, HostListener, inject, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GridOptions,
  Column,
  ColGroupDef,
  ColDef,
  BodyScrollEvent,
  EditableCallbackParams,
  GridApi,
  ICellRendererParams,
  CellClickedEvent,
  ColumnMovedEvent
} from 'ag-grid-community';
import { DecimalPipe } from '@angular/common';
import { catchError, concatMap, delay, first, map, tap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { AddPersonHeaderComponent } from './add-person-header/add-person-header.component';
import { BaseComponent } from '../../../components/base/base.component';
import { Division } from '../../../models/division';
import {
  Earning,
  EmployeePay,
  MasterPayrollObject,
  OpenPaylineDetailsArgs,
  PayGridSessionData,
  Payline,
  PaylineType,
  PayTypes,
  PayrollStartData,
  SelectedPaylineArgs,
  HistoryVoidData,
  AddPaylineRequestModel
} from '../../../models/payroll';
import {
  CompanyDeductionConfig,
  DeductionCalculations
} from '../../../models/company-deduction-config';
import {
  CompanyEarningConfig,
  EarningCalculations,
  SpecialUseEarning
} from '../../../models/company-earning-config';
import { CompanyTaxCodeConfig } from '../../../models/tax-config';
import { CustomHeaderIconComponent } from '../../../components/grid/custom-header-icon/custom-header-icon.component';
import { CustomGridIconComponent } from '../../../components/grid/custom-grid-icon/custom-grid-icon.component';
import { MorePayrollActionsPopup } from '../../../components/grid/more-payroll-actions-popup/more-payroll-actions-popup';
import { GridCheckboxComponent } from '../../../components/grid/grid-checkbox/grid-checkbox.component';
import { PayrollFrequency } from '../../../models/payroll-info';
import { AuditService } from '../../../services/audit.service';
import { CompaniesService } from '../../../services/companies.service';
import { PayrollService } from '../../../services/payroll.service';
import { ModalService } from '../../../services/modal.service';
import { PeopleService } from '../../../services/people.service';
import { SharedService } from '../../../services/shared.service';
import { EnumService } from '../../../services/enum.service';
import { WarningLevel } from '../../../models/error-detail';
import { CellEditorComponent } from '../../../components/grid/cell-editor/cell-editor.component';
import { CellRendererComponent } from '../../../components/grid/cell-renderer/cell-renderer.component';
import { KeyPair, KeyTriple } from '../../../models/base';
import { ModalStates } from '../../../models/modal';
import { PayrollManagement } from '../../../tools/payroll-management';
import { Client } from '../../../models/client';
import { LaborAllocationTypes } from '../../../models/labor-allocation';
import { PayrollGridComponentConfig } from '../../../services/payroll-grid-component-config';
import { GridButtonComponent } from '../../../components/grid/grid-button/grid-button.component';
import { LaborAllocationsComponent } from './labor-allocations/labor-allocations.component';
import { AuthService } from '../../../services/auth.service';
import { BusinessDaysService } from '../../../services/business-days.service';
import { GetTimeInEST, AreDatesEqual } from '../../../utils/dateUtils';
import { LaborAllocationService } from '../../../services/labor-allocation.service';
import { RouteParamsService } from '../../../services/route-params.service';
import { CompanyLocationConfig } from '../../../models/company-location-config';
import { InstanceTypes } from '../../../models/instance-types';
import { AddPaylineTypeComponent } from '../add-payline-type/add-payline-type.component';
import { GetQuarter, GetQuarterEnd, GetQuarterStart } from '../../../utils/quarter-utils';
import { HistoryVoidSelection } from '../add-payline-type/history-void-selection/history-void-selection.component';
import { EditRateModalComponent } from './edit-rate-modal/edit-rate-modal.component';
import { BillingSelectModalComponent } from '../billing/billing-select-modal.component';
import { AclService } from '../../../services/acl.service';

import { Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';
import { ChangeRequestHistoryEntry } from '../../../models/change-requests';
import { ToastService } from '../../../services/toast.service';
import _ from 'underscore';
import { UserPreferencesService } from '../../../services/user-preferences';
import { UserPreferences } from '../../../models/user';

// This enum and interface are used for rendering rate column for payline
enum RateType {
  None,
  Reg,
  Salary,
  Both,
  Skip
}
interface RowRateData {
  Type: RateType;
  SalaryId?: string;
  RegId?: string;
}

@Component({
  selector: 'payroll-grid',
  templateUrl: './payroll-grid.component.html',
  styleUrls: ['./payroll-grid.component.scss'],
  standalone: false
})
export class PayrollGridComponent extends BaseComponent implements OnInit {
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // bug 13993, our current guard is not invoked when the browser window itself is closed/disposed
    // so in the event the user is on the grid and just terminates thier browser window
    // we need to initiate a status on hold request
    // otherwise anyone visting the payroll thereafter will be met
    // with a payroll in progress critical error and will be prevented from resuming...
    return this.payrollMgmt.onPayrollDeactivate(this.payroll, true, this.rowData);
  }

  public addPaylineSub: Subscription;
  public addPersonSub: Subscription;
  public openAddPersonSub: Subscription;
  public openPersonEditorSub: Subscription;
  public openDetailsSub: Subscription;

  public allRowData = [];
  public client: Client;
  public currentColId: string;
  public currentRow: any = null;
  public deductionsGroup: any;
  public divisions: Division[];
  public earningsGroup: any;
  public employeeStatuses = [];
  public clientCode: string;
  public columnDefs = [];
  public columnTypes = {
    immutable: {
      suppressNavigable: true,
      cellClass: 'immutable-cell'
    }
  };

  public selEmployeePay: EmployeePay; // employee pay object passed to detail editor
  public selPersonId: number; // id passed to person editor modal
  public sessionData: PayGridSessionData; // tracked edited cells and paylines that have been calculated since payroll was created.
  public initialPaylineState: Payline; // initial state sent to detail editor
  public defaultCellRendererParams = { auditContext: 'Payroll' };
  public deductionConfigs: Record<string, CompanyDeductionConfig> = {};
  public earningConfigs: Record<string, CompanyEarningConfig> = {};
  public taxConfigs: Record<string, CompanyTaxCodeConfig> = {};
  public gridApi: GridApi<any>;
  public gridHeight = '600px';
  public gridOptions: GridOptions;
  public freqDisplay: string;
  public isGridInEditMode: boolean;
  public isLoading: boolean;
  public jobCodes: KeyPair[];
  public okToPayCount: number = 0;
  public seqNo: number;
  public showPersonEditor: boolean;
  public startData: PayrollStartData;
  public year: number;
  public paylineDetailArgs: OpenPaylineDetailsArgs;
  public paylineErrors = []; // 'View Errors' modal
  public payroll: MasterPayrollObject;
  public render = false;
  public rowClassRules = {
    'error-row': function (params) {
      return params.data.Error;
    },
    'loading-row': function (params) {
      return params.data.IsLoading;
    }
  };
  public rowData = [];
  public columnVizIndicator: string;
  public rowFilterIndicator: string;
  public rowView = 'all';
  public selectedEmployee: number | null = null;
  public showPaylineDetails = false;
  public toggleableColumns = [];
  public searchValue: any;
  public dueDateWarning: string | null = null;
  public userPreferences: UserPreferences;

  // task 25407 - throttle calc payline requests and queue remaining
  private calcPaylineCount = 0;
  private calcPaylineLimit = 6;
  private calcPaylineQueue: { rowData: any; callback: any }[] = [];

  get laborAllocationTypes() {
    return LaborAllocationTypes;
  }

  get paysToCalc() {
    return this.payrollMgmt.uncalculatedPays;
  }

  private readonly toastService = inject(ToastService);

  constructor(
    private aclService: AclService,
    private auditService: AuditService,
    private companyService: CompaniesService,
    private decimalPipe: DecimalPipe,
    private laborAllocService: LaborAllocationService,
    public payrollMgmt: PayrollManagement,
    private payrollService: PayrollService,
    private modalService: ModalService,
    private ngModalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService,
    private enumService: EnumService,
    private peopleService: PeopleService,
    private componentConfig: PayrollGridComponentConfig,
    private authService: AuthService,
    private businessDayService: BusinessDaysService,
    private routeParamsService: RouteParamsService,
    private store: Store,
    private userPrefSvc: UserPreferencesService,
    @Inject('env') private env: any
  ) {
    super();
    this.clientCode = this.route.snapshot.paramMap.get('client_code');
    if (!this.routeParamsService.isInitialized()) {
      this.routeParamsService.setParams(this.route.paramMap);
    }
    this.year = +this.route.snapshot.paramMap.get('year');
    this.seqNo = +this.route.snapshot.paramMap.get('seqNo');
    if (this.route.snapshot.queryParamMap.has('selectedEmployee')) {
      this.selectedEmployee = parseInt(this.route.snapshot.queryParamMap.get('selectedEmployee'));
    }

    this._onGridScroll = _.debounce(this._onGridScroll, 500);
    this.gridOptions = this._getGridOptions();

    // check to make sure this is a valid payroll request
    this.companyService
      .getLocations(this.clientCode)
      .pipe(
        concatMap(locations => {
          return this.companyService.getCompany(this.clientCode).pipe(
            map(resp => {
              this.client = resp;
              return locations;
            })
          );
        }),
        concatMap(locations => {
          return this.companyService.getJobCodes(this.clientCode).pipe(
            map(data => {
              this.jobCodes = data;
              return locations;
            })
          );
        }),
        concatMap(locations => {
          return this.userPrefSvc.getUserPreferences().pipe(
            map(data => {
              this.userPreferences = data ? data : new UserPreferences();
              return locations;
            })
          );
        })
      )
      .subscribe(locationConfigs => {
        this._getPayroll(locationConfigs);
      });

    // task 10058 - generate a list of employee statues to pass along to children
    this.enumService
      .get('EmployeeStatuses')
      .pipe(map(data => (this.employeeStatuses = data)))
      .subscribe();

    this._setColVizRequest = _.debounce(this._setColVizRequest, 2000);
  }

  ngOnInit() {
    // listen for the events fired from more-payroll-actions-popup
    this._listenForSharedServices();
    // us 30507 - fix for holding up/down arrow key
    this._startEditingCell = _.throttle(this._startEditingCell, 100);
    this._handleArrowKeyPress = _.throttle(this._handleArrowKeyPress, 100);
  }

  ngOnDestroy() {
    // unsubscribe from service so when this component
    // is reinitialized there is only 1 subscriber
    this.addPaylineSub.unsubscribe();
    this.addPersonSub.unsubscribe();
    this.openAddPersonSub.unsubscribe();
    this.openDetailsSub.unsubscribe();
    this.openPersonEditorSub.unsubscribe();
  }

  // #region Public Methods
  public areLaborAllocationsEnabled() {
    return (
      this.componentConfig.enableLaborAllocations &&
      this.laborAllocationTypes[this.client.LaborAllocationType] != 'None'
    );
  }

  // return true if any payline is currently getting calculated.
  // us 27344
  // disable the 'review & submit' button to try to prevent
  // race conditions on button press while paylines are getting calculated.
  public arePaylinesCalculating() {
    return this.rowData.find(x => x.IsLoading) != null;
  }

  // task 12487 as the user scrolls,
  // the grid will calculate paylines that come into view.
  public calculateTotalsInView() {
    // get the rows currently displayed on the screen
    let nodes = this.gridApi.getRenderedNodes();

    // get nodes that need to be calculated
    nodes = nodes.filter(
      r => !r.data.IsLoading && this.paysToCalc.includes(r.data.PersonId) && !r.isSelected
    );

    for (let node of nodes) {
      this._initiatePaylineCalculation(node.data);
    }
  }

  public cancelPayroll() {
    const msg = 'Are you sure you want to cancel this payroll?';
    this.modalService.showConfirmModal(msg, 'Do not Cancel Payroll', 'Cancel Payroll');
    this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
      if (confirm) {
        this.payrollMgmt.cancelPayroll(this.clientCode, this.year, this.seqNo).subscribe(() => {
          // set IsCancelled so deactivate guard logic knows
          // not to update the payroll's status
          this.payroll.IsCancelled = true;
          // bug 13674
          // send user back to payroll landing page
          this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
        });
      }
    });
  }

  public closePaylineDetails(details: { pay: EmployeePay; line: Payline; isDirty: boolean }) {
    // the payline detail editor has been closed from its component

    // if no data is passed back, user cancelled out of the payline detail editor
    if (!details) {
      this.showPaylineDetails = false;
      return;
    }

    // update payroll model with the edited values from the detail editor
    const index = this.payroll.EmployeePays.findIndex(x => x.PersonId == details.pay.PersonId);
    const pay = this.payroll.EmployeePays[index];
    const line = pay.Paylines.find(x => x.PaylineId == details.line.PaylineId);

    // task 9324 - if multiple paylines, display notification
    // task 9583 - don't show this message if this is the last
    // payline for an employee
    if (details.pay.Paylines.length > 1 && pay.Paylines.indexOf(line) != pay.Paylines.length - 1) {
      this.toastService.show(
        `All pays for ${this.payrollMgmt.getEmployeeFullName(pay)} ${
          pay.EmployeeNumber
        } have been recalculated`
      );
    }

    this.payroll.EmployeePays[index] = details.pay;

    // apply payroll changes back to the grid so the UI updates
    let rowData = this.rowData.find(
      x => x.PaylineId == details.line.PaylineId && x.PersonId == details.pay.PersonId
    );

    rowData.DetailEdited = details.isDirty;

    this._updateRow(details.line, rowData, details.pay);

    // update totals and all rows assocaited with this employee pay
    details.pay.Paylines.forEach(line => this._setPaylineCalculated(line, details.pay));

    this._updateRowErrors(details.pay.PersonId, details.pay.Paylines);

    this.selEmployeePay = null;
    this.showPaylineDetails = false;
    this._updateOkToPayCount();

    // update cached payroll object in the mgmt service
    this.payrollMgmt.updatePayline(
      this.clientCode,
      this.year,
      this.seqNo,
      rowData.PersonId,
      details.line
    );

    this.payrollMgmt.updatePayGridSessionData(
      this.clientCode,
      this.year,
      this.seqNo,
      this.sessionData
    );

    this.freqDisplay = this.payrollMgmt.payrollFrequencyDisplay(this.payroll.EmployeePays, false);
  }

  public closePersonEditor(params: any) {
    if (!params.refresh) {
      // no changes were made to the person record.
      // close the editor and then do nothing else
      this.showPersonEditor = false;
      return;
    }
    this.auditService.auditPayroll(`payroll-person-edited`, {
      ...this.payrollMgmt.getAuditData(this.payroll)
    });
    // changes were made to this employee in the editor
    if (params.validToPay) {
      this._getUpdatedEmployeePay();
    } else {
      // changes made to the person resulted in an invalid to pay setup
      // remove this person's paylines from grid
      this._removeEmployeeFromGrid();
      this.showPersonEditor = false;
    }

    // bug 18599
    // user can add new department/job code in the person editor
    // refetch the list here so it is up to date
    this.companyService
      .getJobCodes(this.clientCode)
      .pipe(map(data => (this.jobCodes = data)))
      .subscribe();
  }

  // find any rows that still need to have totals calculated.  map those rows
  // into observables to get totals.  forkjoin those observables into a single
  // observable and update our payroll with the results on completion. finally,
  // navigate to the payroll preview component
  public goToPayrollPreview() {
    this.isLoading = true;
    const personIds = [];

    let paysToCalc = this._findUncalculatedPaylines(personIds);

    if (!paysToCalc.length) {
      this.router.navigate([
        `/client/${this.clientCode}/payroll-preview/${this.year}/${this.seqNo}`
      ]);
    } else {
      forkJoin(paysToCalc).subscribe(
        () => {
          this.isLoading = false;

          this._setPaylinesCalculated(personIds);

          this.router.navigate([
            `/client/${this.clientCode}/payroll-preview/${this.year}/${this.seqNo}`
          ]);
        },
        err => {
          this.isLoading = false;
        }
      );
    }
  }

  public onAllocationClick(e) {
    this.payrollMgmt
      .getEmployeePay(this.clientCode, this.year, this.seqNo, e.rowData.PersonId)
      .subscribe(pay => {
        const modalRef = this.ngModalService.open(LaborAllocationsComponent, {
          size: 'xl' as 'lg',
          keyboard: false,
          backdrop: 'static',
          windowClass: 'modal-fit-content'
        });

        const comp = modalRef.componentInstance;
        comp.allocTypeId = this.client.LaborAllocationType;
        comp.clientCode = this.clientCode;
        comp.earningConfigs = this.earningConfigs;
        comp.employeePay = pay;
        comp.jobCodes = this.jobCodes;
        comp.payline = pay.Paylines.find(x => x.PaylineId == e.rowData.PaylineId);

        comp.saveAllocations.subscribe(args => {
          this.saveLaborAllocations(args.payline, args.personId);
        });

        modalRef.result.then(
          // confirm
          () => {},
          // cancel
          () => {
            this._focusRow(e.rowData.PersonId, e.rowData.PaylineId);
          }
        );
      });
  }

  public onRateEditClick(event: CellClickedEvent) {
    const modalRef = this.ngModalService.open(EditRateModalComponent, {
      size: 'xl' as 'lg',
      keyboard: false,
      backdrop: 'static',
      windowClass: 'modal-fit-content'
    });

    const rateData = event.data.RateData as RowRateData;

    var pays = this.payroll.EmployeePays.find(x => x.PersonId == event.data.PersonId);
    var payline = pays.Paylines.find(x => x.PaylineId == event.data.PaylineId);

    var regEarning = payline.Earnings.find(e => e.EarningId == rateData.RegId);
    var salarayEarning = payline.Earnings.find(e => e.EarningId == rateData.SalaryId);

    const comp = modalRef.componentInstance as EditRateModalComponent;

    comp.RegValue =
      regEarning.TemporaryRateAmount == null
        ? regEarning.PermanentRateAmount
        : regEarning.TemporaryRateAmount;
    comp.SalaryValue =
      salarayEarning.TemporaryRateAmount == null
        ? salarayEarning.PermanentRateAmount
        : salarayEarning.TemporaryRateAmount;
    comp.PersonName = `${pays.EmployeeFirstName} ${pays.EmployeeLastName}`;
    comp.IsAdd =
      rateData.Type == RateType.None &&
      ((salarayEarning?.TemporaryRateAmount ?? 0) == 0 ||
        (salarayEarning?.TemporaryRateAmount ?? 0) == 0);

    comp.save.subscribe(args => {
      if (regEarning.PermanentRateAmount != args.RegValue) {
        regEarning.TemporaryRateAmount = args.RegValue;
      } else {
        regEarning.TemporaryRateAmount = null;
      }

      if (salarayEarning.PermanentRateAmount != args.SalaryValue) {
        salarayEarning.TemporaryRateAmount = args.SalaryValue;
      } else {
        salarayEarning.TemporaryRateAmount = null;
      }

      // set both of these, so that the grid renders correctly, and the request is set correctly
      payline.OkToPay = event.data.OkToPay = true;

      this.payrollMgmt.updatePayline(
        this.clientCode,
        this.year,
        this.seqNo,
        event.data.PersonId,
        payline,
        true
      );

      this._initiatePaylineCalculation(event.data);
      this._updateOkToPayCount();

      // redraw the row so that the RateData on the row stays in sync
      this._redrawRow(event.node);
    });
  }

  public onCellKeyDown(params) {
    const code = params.event.code;

    // if OkToPay column is selected and space bar is pressed, toggle value
    if (params.colDef.field === 'OkToPay' && code === 'Space') {
      const rowNode = params.api.getRowNode(params.data.RowId);
      rowNode.setDataValue('OkToPay', !params.data.OkToPay);
    }

    // users have asked to break out of "edit mode" and navigate around the grid
    // when the arrow keys are pressed
    // NOTE: this logic is not necessary for "OkToPay" column
    // which is a custom cell renderer that does not actually enter "edit mode"
    const arrowCodes = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (arrowCodes.includes(code) && params.colDef.field !== 'OkToPay') {
      this._handleArrowKeyPress(code);
    }
  }

  public onRowViewChange(view: string) {
    this.auditService.auditPayroll(`payroll-filter-button-press-${view}`, {
      ...this.payrollMgmt.getAuditData(this.payroll)
    });

    this.rowView = view;
    let rowData = [...this.allRowData];
    let data = [];
    // indicator says something like 'Displaying 1 error row of 100 total rows'
    this.rowFilterIndicator = 'Displaying';
    switch (this.rowView) {
      case 'all': {
        this.rowData = rowData;
        break;
      }
      case 'changed': {
        rowData.forEach(x => {
          const editedCells = this._getEditedCells(x.PersonId, x.PaylineId);
          if (editedCells.length) {
            // cells on this payline have been changed by a user
            data.push(x);
          }
        });
        this.rowData = data.filter(r => !r.Hidden);
        this.rowFilterIndicator += ` ${this.rowData.length} changed ${this.pluralize(
          'row',
          this.rowData.length,
          's'
        )} of`;
        break;
      }
      case 'warnings': {
        rowData.forEach(x => {
          if (x.Warning || x.Info) {
            data.push(x);
          }
        });
        this.rowData = data.filter(r => !r.Hidden);
        this.rowFilterIndicator += ` ${this.rowData.length} warning ${this.pluralize(
          'row',
          this.rowData.length,
          's'
        )} of`;
        break;
      }
      case 'errors': {
        rowData.forEach(x => {
          if (x.Error) {
            data.push(x);
          }
        });
        this.rowData = data.filter(r => !r.Hidden);
        this.rowFilterIndicator += ` ${this.rowData.length} error ${this.pluralize(
          'row',
          this.rowData.length,
          's'
        )} of`;
        break;
      }
    }

    const allCount = this.allRowData.filter(r => !r.Hidden).length;
    this.rowFilterIndicator += ` ${allCount} total ${this.pluralize('row', allCount, 's')}`;
  }

  public print() {
    window.print();
  }

  public saveLaborAllocations(payline: Payline, personId: number) {
    const pay = this.payroll.EmployeePays.find(x => x.PersonId == personId);
    const p = pay.Paylines.find(x => x.PaylineId == payline.PaylineId);
    const i = pay.Paylines.indexOf(p);
    pay.Paylines[i] = payline;

    const row = this.rowData.find(x => x.PersonId == personId && x.PaylineId == payline.PaylineId);
    row.OkToPay = true; // bug 19304

    this._updatePayline(payline, row);
    this._updateOkToPayCount();
    this._initiatePaylineCalculation(row);
    this._focusRow(personId, payline.PaylineId);
  }

  public showBillings() {
    // only show billing modal for off cycle payroll
    if (this.payroll.PayType !== PayTypes.OffCycle) {
      this.goToPayrollPreview();
      return;
    }

    // if user does not have min CSR1 role, navigate directly to preview report
    this.aclService
      .HasMinimumRoleByName(this.authService.getCurrentUserId(), 'CSR1')
      .subscribe(hasRole => {
        if (!hasRole) {
          this.goToPayrollPreview();
          return;
        }

        const modalRef = this.ngModalService.open(BillingSelectModalComponent, {
          size: 'lg',
          backdrop: 'static',
          keyboard: false,
          windowClass: 'centered'
        });

        const c = modalRef.componentInstance;

        c.auditKey = 'off-cycle-billing-override';
        c.payroll = this.payroll;

        c.onDismiss = () => {
          modalRef.dismiss();
        };
        c.onClose = () => {
          modalRef.close();
        };

        modalRef.result.then(
          // confirm
          () => {
            this.goToPayrollPreview();
          },
          // cancel
          () => {}
        );
      });
  }

  public showPaylineErrors(content) {
    this.paylineErrors = [];

    this.payroll.EmployeePays.forEach(pay => {
      pay.Paylines.forEach(line => {
        if (line.OkToPay && line.Errors && line.Errors.length) {
          line.Errors.forEach(message => {
            // show these messages in the 'View Errors' modal
            this.paylineErrors.push({
              Message: message.Message,
              WarningLevel: WarningLevel[message.WarningLevel],
              PersonId: pay.PersonId,
              FirstName: pay.EmployeeFirstName,
              MiddleName: pay.EmployeeMiddleName,
              LastName: pay.EmployeeLastName,
              Id: pay.EmployeeNumber
            });
          });
        }
      });
    });

    // Bug 7702: sorting should follow the company setup; use an arrow function
    // rather than passing the function directly because we need to access
    // ConfigSetupData from the component while inside the comparison function
    this.paylineErrors.sort((left, right) => this._comparePaylineErrors(left, right));

    this.ngModalService
      .open(content, {
        size: 'lg',
        backdrop: 'static',
        keyboard: false
      })
      .result.then(
        result => {},
        () => {}
      );
  }

  public sortPaylineErrors(event, tableId, column) {
    // update column sort icons
    this.sortTableBy(event, tableId, column);

    this.paylineErrors.sort((a, b) => {
      let aCol = a[column];
      let bCol = b[column];

      // sort name by last then first
      if (column === 'Name') {
        aCol = `${a['LastName']} ${a['FirstName']}`;
        bCol = `${b['LastName']} ${b['FirstName']}`;
      }

      if (this.sortDirection === 'desc') {
        return typeof aCol === 'string' ? (bCol > aCol ? 1 : -1) : bCol - aCol;
      } else if (this.sortDirection === 'asc') {
        return typeof aCol === 'string' ? (aCol > bCol ? 1 : -1) : aCol - bCol;
      }
      return 0;
    });
  }

  public toggleColumn(field: string, groupName: string) {
    // if no group name is passed, the column is in the left pinned section
    if (!groupName) {
      this.columnDefs.forEach(col => {
        if (col.field == field) {
          col.hide = !col.hide;

          this._updateColVizPref(col.field, !col.hide);
        }
      });
    } else {
      let colGroup = null;
      let foundFirstVisible = false;

      this.columnDefs.forEach(group => {
        if (group.headerName == groupName) {
          colGroup = group;
          group.children.forEach(col => {
            if (col.field == field) {
              col.hide = !col.hide;
              this._updateColVizPref(col.field, !col.hide);
            }

            // Bug 15306 Always set the first visible column to remain visible when column is collapsed
            if (!col.hide && !foundFirstVisible) {
              foundFirstVisible = true;
              col.columnGroupShow = null;
            } else {
              col.columnGroupShow = 'closed';
            }
          });
        }
      });
    }

    this.gridApi.setGridOption('columnDefs', this.columnDefs);

    this._autoSizeAllColumns();
    this._setColumnVizIndicator();
  }

  public toggleColumnGroup(evnt, groupName: string) {
    let group;
    this.columnDefs.forEach(g => {
      if (g.headerName == groupName) {
        group = g;
      }
    });

    group.children.forEach(child => {
      // if group checkbox is checked, hide all
      // else if group checkbox is unchecked, show all
      child.hide = !evnt.currentTarget.checked;
      this._updateColVizPref(child.field, !child.hide);
    });

    this.gridApi.setGridOption('columnDefs', this.columnDefs);

    this._autoSizeAllColumns();
    this._setColumnVizIndicator();
  }

  //
  // Check to see if column is the last remaining visible column in its group.
  //
  public isColumnFilterDisabled(e: ColDef, groupName: string): boolean {
    if (e.hide) {
      return false;
    }
    const group = this.columnDefs.find(col => col.headerName == groupName);
    if (group == null) {
      return false;
    }

    var multipleActive = group.children.some(col => col != e && !col.hide);
    if (multipleActive) {
      return false;
    }

    return true;
  }

  // #endregion

  // #region PRIVATE METHODS
  private _addHistoryVoidPayline(args: SelectedPaylineArgs, v: HistoryVoidSelection) {
    const reqBody = {
      HistoryVoidData: new HistoryVoidData(
        v.Payroll.Year,
        v.Payroll.SequenceNumber,
        v.Payline.PaylineId,
        v.Payline.OverrideCheckNumber
      ),
      PaylineType: PaylineType.Void
    };

    this.payrollMgmt
      .addPayline(this.clientCode, this.year, this.seqNo, args.PersonId, reqBody)
      .subscribe(resp => {
        // use payline data from employee pays history endpoint
        // but keep the newly generated paylineId, paylineSeq & historyVoidData
        args.Payline.PaylineId = resp.line.PaylineId;
        args.Payline.HistoryVoidData = resp.line.HistoryVoidData;
        args.Payline.PaylineSeq = resp.line.PaylineSeq;
        args.Payline.OkToPay = true;

        const pIdx = resp.pay.Paylines.indexOf(resp.line);
        resp.pay.Paylines[pIdx] = args.Payline;

        // update employee pay record
        const emp = this.payroll.EmployeePays.find(x => x.PersonId == args.PersonId);
        const index = this.payroll.EmployeePays.indexOf(emp);
        this.payroll.EmployeePays[index] = resp.pay;

        this.payrollMgmt.updateEmployeePay(
          this.clientCode,
          this.year,
          this.seqNo,
          this.payroll.EmployeePays[index]
        );

        this._addPaylineToGrid(resp.pay, resp.pay.Paylines[pIdx]);
        this._setPaylinesCalculated([resp.pay.PersonId]);
        this._updateOkToPayCount();
      });
  }

  // the add payline dropdown option was selected
  private _addPayline(args: SelectedPaylineArgs) {
    var paylineTypes = this.payrollMgmt.getAllowedPaylineTypes();

    if (paylineTypes.length <= 1) {
      // All users can create regular payline types, if that is all we can do, just add it
      this._addRegularPayline(args);
      return;
    }
    if (!this.startData.QuarterStatus?.History) {
      this._openAddPaylineModal(args, []);
      return;
    }

    // Task #26425 - fire API call to fetch employee pay history for just this employee
    // if any paylines are eligible for void, enable the 'History Void' option
    // Task # 26952 - only fetch eligible voids from payrolls in the current year and quarter
    const quarter = GetQuarter(new Date(this.payroll.PayDate));

    const start = GetQuarterStart(quarter, this.payroll.Year).toISOString().slice(0, 10);
    const end = GetQuarterEnd(quarter, this.payroll.Year).toISOString().slice(0, 10);

    this.payrollService
      .getPayrollHistoryByEmployee(this.clientCode, args.PersonId, start, end)
      .subscribe(resp => {
        // BUG 26801 - filter response to exclude any void paylines already added to this payroll
        const eligibleVoids = this._getEligiblePaylineVoids(resp);
        this._openAddPaylineModal(args, eligibleVoids);
      });
  }

  private _addPaylineToGrid(pay: EmployeePay, line: Payline) {
    // bug 27400 - clear search filter when adding a new payline
    this.gridApi.setGridOption('quickFilterText', '');
    this.searchValue = '';

    const row = this._buildRow(line, pay);

    // sort the employee's paylines by payline type
    const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);
    const sortByLastName = this.userPrefSvc.getPayrollEmployeeSortByLastName(
      this.clientCode,
      this.userPreferences
    );
    this.payrollMgmt.sortEmployeePays(this.payroll.EmployeePays, setupData, sortByLastName);
    this.payrollMgmt.sortPaylinesBySeq(this.payroll, pay);

    // get row index to add new payline to
    const paylines = this.payroll.EmployeePays.flatMap(e => e.Paylines);
    const payline = paylines.find(x => x.PaylineId == line.PaylineId);
    const rowIndex = paylines.indexOf(payline);

    // add new row to the grid
    this.gridApi.applyTransaction({
      add: [row as any],
      addIndex: rowIndex
    });

    this.rowData.splice(rowIndex, 0, row);

    this.allRowData = [...this.rowData];
    this.rowFilterIndicator = `Displaying ${
      this.allRowData.filter(r => !r.Hidden).length
    } total rows`;

    this._autoSizeGridHeight();
    this._refreshCells();

    this.payrollMgmt.updatePayGridSessionData(
      this.clientCode,
      this.year,
      this.seqNo,
      this.sessionData
    );

    if (line.PaylineType == PaylineType.Void) {
      row.OkToPay = true;
      this._initiatePaylineCalculation(row);
    }
  }

  private _addPersonToPayroll(pay: EmployeePay) {
    this.payrollMgmt.addPerson(this.clientCode, this.year, this.seqNo, pay).subscribe(payroll => {
      this.payroll = payroll;

      // bug 9794, set default sorting on the grid
      // so new row is in the correct position
      this.gridApi.applyColumnState({
        defaultState: { sort: null }
      });

      // sort the paylines the same way as on page load
      const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);
      const sortByLastName = this.userPrefSvc.getPayrollEmployeeSortByLastName(
        this.clientCode,
        this.userPreferences
      );
      this.payrollMgmt.sortEmployeePays(this.payroll.EmployeePays, setupData, sortByLastName);

      // add new payline to the grid, based on the sorted index from the operation above
      this._addPaylineToGrid(pay, pay.Paylines[0]);

      // scroll to the new payline and focus on the first editable input
      this._focusRow(pay.PersonId);
      this.auditService.auditPayroll(`payroll-person-added`, {
        ...this.payrollMgmt.getAuditData(this.payroll),
        PersonId: pay.PersonId
      });
    });
  }

  private _addRegularPayline(args: SelectedPaylineArgs) {
    this.payrollMgmt
      .addPayline(
        this.clientCode,
        this.year,
        this.seqNo,
        args.PersonId,
        new AddPaylineRequestModel(PaylineType.Normal)
      )
      .subscribe(resp => {
        const emp = this.payroll.EmployeePays.find(x => x.PersonId == args.PersonId);
        const index = this.payroll.EmployeePays.indexOf(emp);
        this.payroll.EmployeePays[index] = resp.pay;

        this._addPaylineToGrid(resp.pay, resp.line);

        this.auditService.auditPayroll(`payroll-payline-added`, {
          ...this.payrollMgmt.getAuditData(this.payroll),
          PaylineId: resp.line.PaylineId
        });
      });
  }

  private _autoSizeAllColumns() {
    // auto size all columns to fit the width of the content
    // if column virtualization is on, only columns in DOM be auto sized
    const allColumnIds = [];
    this.gridApi.getColumns()?.forEach(column => {
      allColumnIds.push(column.getId());
    });

    this.gridApi.autoSizeColumns(allColumnIds);
  }

  private _autoSizeGridHeight() {
    // NOTE: this is a workaround to set the grid height to fit the number of rows
    // grid header rows (with top group row) ~100px
    // each data row is ~45px
    // 20px for horizonal scroll bar
    // max height currently set to 600px
    var header = 100;
    var rows = this.rowData.length * 45;
    var scrollbar = 20;
    var noRows = this.rowData.length == 0 ? 80 : 0;
    this.gridHeight = header + rows + scrollbar + noRows + 'px';
  }

  private _bringFocusBack() {
    // sometimes needed after redrawing rows
    let c = this.gridApi.getFocusedCell();
    if (c) {
      // clear current row tracker data so the cell starts editing as it gains focus
      this.currentRow = this.currentColId = null;
      this.gridApi.setFocusedCell(c.rowIndex, c.column);
    }
  }

  private _buildAllocationColumn() {
    if (!this.areLaborAllocationsEnabled()) {
      return;
    }

    // put this to the left of the rate column
    let rate = this.columnDefs.find(x => x.headerName == 'Rate');
    let i = this.columnDefs.indexOf(rate);
    const col = {
      field: 'Allocation',
      headerName: 'Allocate Pay',
      type: ['immutable'],
      sortable: false,
      pinned: 'left',
      hide: this._hideColFromPrefs('Allocation') ?? false,
      maxWidth: 115,
      tooltipField: 'Allocate Pay',
      cellStyle: { padding: '0 10px' },
      cellRendererSelector: params => {
        const pay = this.payroll.EmployeePays.find(x => x.PersonId == params.data.PersonId);
        const payline = pay.Paylines.find(line => line.PaylineId == params.data.PaylineId);

        if (payline == null) {
          // this payline was likely just deleted
          return null;
        }

        const label = this.payrollMgmt.hasLaborAllocations(pay, payline) ? 'Update' : 'Add';

        if ([PaylineType.Normal, PaylineType.NoCheck].includes(payline.PaylineType)) {
          return {
            component: GridButtonComponent,
            params: {
              label: label,
              onClick: this.onAllocationClick.bind(this),
              isLoading: params.data.IsLoading
            }
          };
        } else {
          return null;
        }
      }
    };

    this.columnDefs.splice(i, 0, col);
    this.toggleableColumns.push(col);
  }

  private _buildDeductionColumns() {
    const deductionGroup: ColGroupDef = {
      headerName: 'Deductions',
      children: []
    };

    const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);
    const deductions = setupData.Deductions.sort(
      (a: CompanyDeductionConfig, b: CompanyDeductionConfig) => {
        return a.AppearanceSequence - b.AppearanceSequence;
      }
    );

    deductions.forEach(deduction => {
      // task 7559 - only display deductions where config.DisplayInGrid == true
      if (!deduction.DisplayInGrid) {
        return;
      }

      let cellParams = { mode: 'currency', decimals: 2 };

      if (deduction.IsPercentageDeduction) {
        cellParams = { mode: 'percent', decimals: 4 };
      } else if (deduction.CalculationMethod == DeductionCalculations.Rate) {
        cellParams = { mode: 'per-hour', decimals: 2 };
      }

      deductionGroup.children.push({
        field: deduction.Name,
        headerName: deduction.Name,
        editable: params => {
          // if there is a key available in this employee's payline and
          // payline is not currently loading/validating, allow editing.
          // see _buildGridDeductionData to see what gets set
          return this._isDeductionCellEnabled(params);
        },
        cellEditor: CellEditorComponent,
        cellEditorParams: { ...cellParams },
        cellRenderer: CellRendererComponent,
        cellRendererParams: params => this._setCellRendererParams(params, cellParams),
        columnGroupShow: 'closed',
        minWidth: 115,
        hide:
          this._hideColFromPrefs(deduction.Name) ??
          this.payroll.Config.HoldDeductions.includes(deduction.Id),
        cellStyle: params => this._setCellStyle(params, {}),
        headerComponent: CustomHeaderIconComponent,
        cellDataType: 'text'
      });
    });

    // find the first visible column and set it to be visible when the group is collapsed
    let firstColumnVisible = false;
    deductionGroup.children.forEach(child => {
      var col = child as ColDef;
      if (col != null && !col.hide && !firstColumnVisible) {
        firstColumnVisible = true;
        col.columnGroupShow = null;
      } else {
        col.columnGroupShow = 'closed';
      }
    });

    this.deductionsGroup = deductionGroup;

    return deductionGroup;
  }

  private _buildDivisionColumns() {
    // get index of name column.
    // div/dept columns are spliced in after that.
    let name = this.columnDefs.find(x => x.headerName == 'Name');
    let i = this.columnDefs.indexOf(name) + 1;

    // if there are divisions/departments, add those column in now
    const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);

    // US 38537 - hide div column if client only has 1
    if (setupData.HasDivisions && this.divisions?.length > 1) {
      // rendering division/department column(s) if client has any
      const divCol = {
        field: 'DivisionDisplayId',
        headerName: 'Div',
        type: ['immutable'],
        sortable: true,
        pinned: 'left',
        hide: this._hideColFromPrefs('DivisionDisplayId') ?? false,
        tooltipField: 'DivisionName'
      };

      this.columnDefs.splice(i, 0, divCol);
      this.toggleableColumns.push(divCol);

      i++;
    }

    // US 38538 - hide dept column if client only has 1
    let deptCount = 0;

    if (this.divisions?.length > 0) {
      for (let div of this.divisions) {
        deptCount += div.Departments?.length || 0;
      }
    }

    if (setupData.HasDepartments && deptCount > 1) {
      const deptCol = {
        field: 'DepartmentDisplayId',
        headerName: 'Dept',
        type: ['immutable'],
        sortable: true,
        pinned: 'left',
        hide: this._hideColFromPrefs('DepartmentDisplayId') ?? false,
        tooltipField: 'DepartmentName'
      };

      this.columnDefs.splice(i, 0, deptCol);
      this.toggleableColumns.push(deptCol);
    }
  }

  private _buildEarningColumns() {
    const earningsGroup: ColGroupDef = {
      headerName: 'Earnings',
      children: []
    };

    const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);

    const earnings = setupData.Earnings.sort((a: CompanyEarningConfig, b: CompanyEarningConfig) => {
      return a.AppearanceSequence - b.AppearanceSequence;
    });

    earnings.forEach(earning => {
      // task 7882 - only display earnings where config.DisplayInGrid == true
      if (!earning.DisplayInGrid) {
        return;
      }

      let cellParams = {};

      const e: ColDef = {
        field: earning.Name,
        headerName: earning.Name,
        editable: params => {
          // if there is a key available in this employee's payline and
          // payline is not currently loading/validating, allow editing.
          // see _buildGridEarningData for more info
          const pay = this.payroll.EmployeePays.find(x => x.PersonId == params.data.PersonId);
          const line = pay.Paylines.find(x => x.PaylineId == params.data.PaylineId);
          const header = params.column.getColDef().headerName; // earning code
          const config = Object.values(this.earningConfigs).find(x => x.Name == header);
          return this._isEarningCellEnabled(params, line, config);
        },
        cellEditor: CellEditorComponent,
        valueFormatter: params => {
          const opts = {
            isPlaceholder: false,
            placeholder: null
          };

          const pay = this.payroll.EmployeePays.find(x => x.PersonId == params.data.PersonId);
          if (pay == null) {
            return '';
          }
          const line = pay.Paylines.find(x => x.PaylineId == params.data.PaylineId);
          if (line == null) {
            return '';
          }
          const earn = line.Earnings.find(x => x.EarningId == earning.Id);

          const decimalPipe = this.decimalPipe;

          if (
            !this.payrollMgmt.earningRateAllocated(line, earning) &&
            !this.payrollMgmt.earningHoursAllocated(line, earning)
          ) {
            opts.isPlaceholder = false;
            opts.placeholder = null;
          } else {
            // US 19922
            if (params.colDef?.field == `${earning.Name}Rate`) {
              opts.placeholder = earning.IsPercentageEarning
                ? null
                : this.laborAllocService.CalculateTotalRate(earn, line);
            } else if (params.colDef?.field == `${earning.Name}Hours`) {
              opts.placeholder = this.laborAllocService.CalculateTotalHours(earn, line);
            }
            opts.isPlaceholder = true;
          }

          const formatPlaceholder = function () {
            if (opts.placeholder == null) {
              return '---';
            }

            return format(opts.placeholder);
          };

          const formatNumber = function () {
            if (opts.isPlaceholder) {
              return formatPlaceholder();
            }

            if (!params.value && params.value !== 0) {
              return '';
            }

            return format(params.value);
          };

          const format = function (value) {
            const d = earning.CalculationMethod == EarningCalculations['Rate'] ? 4 : 2;
            return decimalPipe.transform(value, `1.2-${d}`);
          };

          let num = formatNumber();

          const fixedDollar =
            earning.CalculationMethod === EarningCalculations['Fixed Dollar'] &&
            earning.SpecialUseEarning === SpecialUseEarning.Other;

          const currency =
            params.colDef?.field == `${earning.Name}Rate` &&
            (earning.CalculationMethod === EarningCalculations['Rate'] || fixedDollar);

          return num && currency ? `$  ${num}` : num;
        },
        cellRendererSelector: params => {
          // this fires when a cell is clicked and provides the row data as the param
          const pay = this.payroll.EmployeePays.find(x => x.PersonId == params.data.PersonId);
          const line = pay.Paylines.find(x => x.PaylineId == params.data.PaylineId);
          const header = params.column.getColDef().headerName; // earning code
          const config = Object.values(this.earningConfigs).find(x => x.Name == header);
          if (line == null || this._isEarningCellEnabled(params, line, config)) {
            return null; // use valueFormatter
          }

          const earn = line.Earnings.find(x => x.EarningId == earning.Id);
          let placeholder = 0;

          if (
            !this.payrollMgmt.earningRateAllocated(line, earning) &&
            !this.payrollMgmt.earningHoursAllocated(line, earning)
          ) {
            return {
              component: CellRendererComponent
            };
          }

          // US 19922
          if (params.colDef?.field == `${earning.Name}Rate`) {
            placeholder = earning.IsPercentageEarning
              ? null
              : this.laborAllocService.CalculateTotalRate(earn, line);
          } else if (params.colDef?.field == `${earning.Name}Hours`) {
            placeholder = this.laborAllocService.CalculateTotalHours(earn, line);
          }

          return {
            component: CellRendererComponent,
            params: {
              isPlaceholder: true,
              placeholder: placeholder
            }
          };
        },
        columnGroupShow: 'closed',
        minWidth: 115,
        cellStyle: params => this._setCellStyle(params, {}),
        headerComponent: CustomHeaderIconComponent,
        cellDataType: 'text'
      };

      // bug 7781 - render both hours and rate earning columns
      // if calc method is Rate && earning is not reg
      const renderBoth =
        earning.CalculationMethod === EarningCalculations['Rate'] && earning.Name !== 'REG';

      if (
        renderBoth ||
        (earning.CalculationMethod === EarningCalculations['Fixed Dollar'] &&
          earning.SpecialUseEarning === SpecialUseEarning.Other)
      ) {
        cellParams = {
          mode: 'currency',
          decimals: earning.CalculationMethod == EarningCalculations['Rate'] ? 4 : 2
        };
        e.headerComponentParams = { icon: 'fas fa-dollar-sign' };
        e.field = `${earning.Name}Rate`;
        e.hide =
          this._hideColFromPrefs(`${earning.Name}Rate`) ??
          this.payroll.Config.HoldEarnings.includes(earning.Id);
        e.cellEditorParams = { ...cellParams };
        e.cellRendererParams = { ...cellParams };

        earningsGroup.children.push({ ...e });
      }

      if (earning.CanSetHours || renderBoth) {
        cellParams = {
          mode: 'number',
          decimals: earning.CalculationMethod == EarningCalculations['Rate'] ? 4 : 2
        };

        e.headerComponentParams = { icon: 'far fa-clock' };
        e.field = `${earning.Name}Hours`;
        e.hide =
          this._hideColFromPrefs(`${earning.Name}Hours`) ??
          this.payroll.Config.HoldEarnings.includes(earning.Id);
        // e.cellRenderer = CellRendererComponent;
        e.cellEditorParams = { ...cellParams };
        e.cellRendererParams = { ...cellParams };

        earningsGroup.children.push({ ...e });
      }
    });

    // find the first visible column and set it to be visible when the group is collapsed
    let firstColumnVisible = false;
    earningsGroup.children.forEach(child => {
      var col = child as ColDef;
      if (col != null && !col.hide && !firstColumnVisible) {
        firstColumnVisible = true;
        col.columnGroupShow = null;
      } else {
        col.columnGroupShow = 'closed';
      }
    });

    this.earningsGroup = earningsGroup;

    return earningsGroup;
  }

  private _buildGrid() {
    this._buildGridDefinitions();
    this._buildGridRowData();
    this._gridDataReady();
  }

  private _buildGridDeductionData(row: any, line: Payline, pay: EmployeePay) {
    line.Deductions.forEach(deduction => {
      const config = this.deductionConfigs[deduction.DeductionCode];

      // only add deductions that should be displayed in the grid
      if (!config.DisplayInGrid) {
        return;
      }

      // task 7879 - disable deduction editing and hide values if
      // calcMethod is 2 (Simple Matching) or 7 (Tiered Matching)
      // enable and display for calc methods 3, 4, 5, 6
      const displayRate = [
        DeductionCalculations.Rate,
        DeductionCalculations['Percent of Earning'],
        DeductionCalculations['Percent of Deduction'],
        DeductionCalculations['Percent of Gross'],
        DeductionCalculations['Percent of Net']
      ].includes(config.CalculationMethod);

      let tempProp = null;
      let permProp = null;

      // determine if the data should be pulled from the
      // Temporary/Permanent Amount or Temporary/Permanent Rate
      if (config.CalculationMethod === DeductionCalculations.Fixed) {
        tempProp = deduction.TemporaryAmount;
        permProp = deduction.PermanentAmount;
      } else if (displayRate) {
        tempProp = deduction.TemporaryRate;
        permProp = deduction.PermanentRate;
      }

      // Bug 8251. if cell is cleared by user. 0 is passed
      // over the wire and that change should persist in the grid.
      if (tempProp || parseInt(tempProp) == 0) {
        // temporary amount is 0, it was cleared by the user
        row[config.Name] = tempProp;
      } else {
        // both temp and perm values are empty - leave cell blank
        row[config.Name] = permProp || null;
      }
    });
  }

  private _buildGridDefinitions() {
    const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);
    setupData.Deductions.forEach(config => {
      this.deductionConfigs[config.Id] = config;
    });

    setupData.Earnings.forEach(config => {
      this.earningConfigs[config.Id] = config;
    });

    this.columnDefs = [
      {
        field: 'OkToPay',
        headerName: 'OK to Pay',
        cellRenderer: GridCheckboxComponent,
        sortable: false,
        pinned: 'left',
        maxWidth: 100,
        cellStyle: params => this._setCellStyle(params)
      },
      {
        field: 'ErrorsAndWarningsSort',
        headerName: '',
        cellRenderer: CustomGridIconComponent,
        type: ['immutable'],
        sortable: false,
        pinned: 'left',
        maxWidth: 75,
        tooltipField: 'Message'
      },
      {
        field: 'PaylineTypeDisplay',
        headerName: 'Type',
        type: ['immutable'],
        sortable: false,
        pinned: 'left',
        maxWidth: 75
      },
      {
        field: 'MoreActions',
        headerName: 'Action',
        cellClass: ['actions-button-cell'],
        cellRenderer: MorePayrollActionsPopup,
        type: ['immutable'],
        editable: false,
        sortable: false,
        pinned: 'left',
        maxWidth: 80
      },
      {
        field: 'EmployeeNumber',
        headerName: 'Id',
        type: ['immutable'],
        sortable: true,
        pinned: 'left',
        hide: this._hideColFromPrefs('EmployeeNumber') ?? false
      },
      {
        headerName: 'Name',
        headerGroupComponent: AddPersonHeaderComponent,
        headerGroupComponentParams: {
          clientCode: this.clientCode,
          sortByLast: this.userPrefSvc.getPayrollEmployeeSortByLastName(
            this.clientCode,
            this.userPreferences
          ),
          callback: userPreferences => this._handleEmployeeSortPrefChange(userPreferences)
        },
        children: [
          {
            field: 'Name',
            type: ['immutable'],
            sortable: true,
            sortingOrder: ['asc', 'desc', null],
            pinned: 'left',
            // when user preferences dictate - sort by last name
            // but continue to display FirstName LastName
            comparator: (valueA, valueB, nodeA, nodeB) => {
              if (valueA == valueB) {
                return 0;
              }

              const personA = this.payroll.EmployeePays.find(
                x => x.EmployeeNumber == nodeA.data.EmployeeNumber
              );
              const personB = this.payroll.EmployeePays.find(
                x => x.EmployeeNumber == nodeB.data.EmployeeNumber
              );

              const sortByLast = this.userPrefSvc.getPayrollEmployeeSortByLastName(
                this.clientCode,
                this.userPreferences
              );

              const personAName = sortByLast
                ? this.payrollMgmt.getEmployeeLastFirstName(personA)
                : this.payrollMgmt.getEmployeeFullName(personA);
              const personBName = sortByLast
                ? this.payrollMgmt.getEmployeeLastFirstName(personB)
                : this.payrollMgmt.getEmployeeFullName(personB);

              return personAName.toLowerCase() > personBName.toLowerCase() ? 1 : -1;
            }
          }
        ]
      },
      {
        field: 'Frequency',
        headerName: 'Frequency',
        type: ['immutable'],
        pinned: 'left',
        hide: this._hideColFromPrefs('Frequency') ?? false,
        sortable: true,
        // sort pay frequency based on the enum order
        // which is most frequency to least
        comparator: (valueA, valueB) => {
          if (valueA == valueB) {
            return 0;
          }

          return Object.values(PayrollFrequency).indexOf(valueA) >
            Object.values(PayrollFrequency).indexOf(valueB)
            ? 1
            : -1;
        }
      },
      {
        field: 'Rate',
        cellDataType: 'text',
        headerName: 'Rate',
        pinned: 'left',
        editable: params => {
          var rateData = params.data.RateData as RowRateData;

          let empPay = this.payroll.EmployeePays.find(p => p.PersonId == params.data.PersonId);
          let payline = empPay.Paylines.find(l => l.PaylineId == params.data.PaylineId);

          if (
            this.areLaborAllocationsEnabled() &&
            this.payrollMgmt.hasLaborAllocations(empPay, payline)
          ) {
            return false;
          }

          if (params.data.IsLoading || params.data.DetailEdited) {
            return false;
          }

          const allowedPaylineTypes = [PaylineType.Normal];

          if (!allowedPaylineTypes.includes(params.data.PaylineType)) {
            return false;
          }

          if (params.data.Rate == null) {
            return false;
          }

          return rateData.Type == RateType.Reg || rateData.Type == RateType.Salary;
        },
        cellEditor: CellEditorComponent,
        cellEditorParams: { mode: 'currency', decimals: 2 },
        cellClassRules: {
          'edit-rate-button': params => this._rateColumnIsButton(params),
          'edit-rate-active': params => this._rateColumnIsActiveButton(params)
        },
        cellRendererParams: params => {
          var rateData = params.data.RateData as RowRateData;

          if (rateData.Type == RateType.Both) {
            return {
              isPlaceholder: true,
              placeholder: 'Both',
              mode: 'text'
            };
          }

          var mode = '';
          switch (rateData.Type) {
            case RateType.Reg:
              mode = 'per-hour';
              break;
            case RateType.Salary:
              mode = 'currency';
              break;
          }
          return {
            isPlaceholder: true,
            placeholder: params.data.Rate,
            mode: mode
          };
        },
        cellRendererSelector: params => {
          var rateData = params.data.RateData as RowRateData;
          if (params.data.IsLoading || params.data.DetailEdited) {
            if (rateData.Type == RateType.Reg || rateData.Type == RateType.Salary) {
              return {
                component: CellRendererComponent
              };
            } else {
              return {
                component: CellRendererComponent,
                params: {}
              };
            }
          }

          return null;
        },
        onCellClicked: (event: CellClickedEvent) => {
          var rateData = event.data.RateData as RowRateData;
          let empPay = this.payroll.EmployeePays.find(p => p.PersonId == event.data.PersonId);
          let payline = empPay.Paylines.find(l => l.PaylineId == event.data.PaylineId);

          if (
            event.data.IsLoading ||
            event.data.DetailEdited ||
            this.payrollMgmt.hasLaborAllocations(empPay, payline) ||
            !this._rateColumnIsAllowedPaylineType(event)
          ) {
            return;
          }

          if (rateData.Type == RateType.Both || rateData.Type == RateType.None) {
            this.onRateEditClick(event);
          }
        },
        valueFormatter: params => {
          var rateData = params.data.RateData as RowRateData;
          let empPay = this.payroll.EmployeePays.find(p => p.PersonId == params.data.PersonId);
          let payline = empPay.Paylines.find(l => l.PaylineId == params.data.PaylineId);

          var hasLaborAllocations = this.payrollMgmt.hasLaborAllocations(empPay, payline);

          if (this._rateColumnIsAllowedPaylineType(params) && !hasLaborAllocations) {
            switch (rateData.Type) {
              case RateType.None:
                var regEarning = payline.Earnings.find(e => e.EarningId == rateData.RegId);
                var salarayEarning = payline.Earnings.find(e => e.EarningId == rateData.SalaryId);
                if (regEarning != null && salarayEarning != null) {
                  if (
                    regEarning?.TemporaryRateAmount > 0 &&
                    salarayEarning?.TemporaryRateAmount > 0
                  ) {
                    return 'Both';
                  }

                  if (regEarning?.TemporaryRateAmount > 0) {
                    return this._formatRegRateColumn(regEarning.TemporaryRateAmount);
                  }

                  if (salarayEarning?.TemporaryRateAmount > 0) {
                    return this._formatSalaryRateColumn(salarayEarning.TemporaryRateAmount);
                  }
                }
                return 'Add Rate';
              case RateType.Both:
                return 'Both';
            }
          }

          var rate = this._rateColumnGetRate(params);

          if (rate.IsBoth) {
            return 'Both';
          }
          if (rate.Rate == null) {
            return '';
          }

          if (rateData.Type == RateType.Reg) {
            return this._formatRegRateColumn(rate.Rate);
          } else {
            return this._formatSalaryRateColumn(rate.Rate);
          }
        },
        hide: this._hideColFromPrefs('Rate') ?? false
      },
      this._buildEarningColumns(),
      this._buildDeductionColumns()
    ];

    const toggleCols = ['EmployeeNumber', 'PaylineTypeDisplay', 'Rate', 'Frequency'];

    this.toggleableColumns.push(...this.columnDefs.filter(x => toggleCols.includes(x.field)));

    this._buildDivisionColumns();
    this._buildAllocationColumn();
  }

  // pass in the payroll frequency id and get back a sort index
  private _buildGridEarningData(row: any, line: Payline, pay: EmployeePay) {
    // rules for the 'Rate' column
    // if an employee has both a non zero Salary and Reg earning rate - display 'both'
    // else display either one if available. for REG, append '/hr'
    let salary: Earning = null;
    let reg: Earning = null;
    let setupForSalary = false;
    let setupForReg = false;

    line.Earnings.forEach(earning => {
      // earning columns are named as such
      // {earningName}Hours, // {earningName}Rate

      const config = this.earningConfigs[earning.EarningId];

      // bug 7781 - render both hours and rate earning columns
      // if calc method is Rate && earning is not reg
      const renderBoth =
        config.CalculationMethod === EarningCalculations['Rate'] && config.Name !== 'REG';

      if (renderBoth || config.CanSetHours) {
        row[`${config.Name}Hours`] = earning.Hours;
      }

      if (
        renderBoth ||
        (config.CalculationMethod === EarningCalculations['Fixed Dollar'] &&
          config.SpecialUseEarning == SpecialUseEarning.Other)
      ) {
        row[`${config.Name}Rate`] =
          // Bug 8251. if cell is cleared by user. 0 is passed
          // over the wire and that change should persist in the grid.
          earning.TemporaryRateAmount || parseInt(`${earning.TemporaryRateAmount}`) == 0
            ? earning.TemporaryRateAmount
            : earning.PermanentRateAmount || '';
      }

      if (config.SpecialUseEarning == SpecialUseEarning.Salary) {
        setupForSalary = true;
        if (earning.PermanentRateAmount > 0 || earning.CurrentAmount > 0) {
          salary = earning;
        }
      } else if (config.SpecialUseEarning == SpecialUseEarning.Regular) {
        setupForReg = true;
        if (earning.PermanentRateAmount > 0 || earning.CurrentAmount > 0) {
          reg = earning;
        }
      }
    });

    let rateData: RowRateData = {
      Type: RateType.None
    };

    if (salary !== null && reg != null) {
      rateData = {
        Type: RateType.Both,
        SalaryId: salary.EarningId,
        RegId: reg.EarningId
      };
    } else if (salary) {
      rateData = {
        Type: RateType.Salary,
        SalaryId: salary.EarningId
      };
      if (this.payrollMgmt.hasLaborAllocations(pay, line)) {
        row.Rate = this.laborAllocService.CalculateTotalRate(salary, line);
      } else {
        row.Rate = salary.TemporaryRateAmount ?? salary.PermanentRateAmount;
      }
    } else if (reg) {
      rateData = {
        Type: RateType.Reg,
        RegId: reg.EarningId
      };

      if (this.payrollMgmt.hasLaborAllocations(pay, line)) {
        row.Rate = this.laborAllocService.CalculateTotalRate(reg, line);
      } else {
        row.Rate = reg.TemporaryRateAmount ?? reg.PermanentRateAmount;
      }
    } else if (setupForReg && setupForSalary) {
      var salEarning = Object.values(this.earningConfigs).find(
        e => e.SpecialUseEarning == SpecialUseEarning.Salary
      );
      var regEarning = Object.values(this.earningConfigs).find(
        e => e.SpecialUseEarning == SpecialUseEarning.Regular
      );

      rateData = {
        Type: RateType.None,
        RegId: regEarning?.Id,
        SalaryId: salEarning?.Id
      };
    } else {
      rateData = {
        Type: RateType.Skip
      };
    }

    row.RateData = rateData;
  }

  private _buildGridRowData() {
    // populate grid with payroll data
    const rows = [];
    this.payroll.EmployeePays.forEach(pay => {
      pay.Paylines.forEach(line => {
        const row = this._buildRow(line, pay);
        rows.push(row);
      });
    });

    this.rowData = rows;
    this.allRowData = [...this.rowData];
    this.rowFilterIndicator = `Displaying ${
      this.allRowData.filter(r => !r.Hidden).length
    } total rows`;
  }

  private _buildRow(line: Payline, pay: EmployeePay) {
    // if changes were made in the detail editor, the row is locked down in the grid
    const rowDetailEdited = this.sessionData.DetailedRowIds.includes(
      `person-${pay.PersonId}-payline-${line.PaylineId}`
    );

    const editedCells = this._getEditedCells(pay.PersonId, line.PaylineId);

    const department = this._getEmployeeDepartment(pay.DepartmentId);
    const division = this._getEmployeeDivision(pay.DivisionId);

    const row = {
      // we need a unique Id attached to the row data.
      // paylineId is only unique within that employeePay
      // construct a syntentic unique Id from these two properties
      RowId: `person-${pay.PersonId}-payline-${line.PaylineId}`,
      PaylineId: line.PaylineId,
      OkToPay: line.OkToPay,
      EmployeeNumber: pay.EmployeeNumber,
      Name: this.payrollMgmt.getEmployeeFullName(pay),
      Frequency: line.PayFrequencyTaxOverride
        ? PayrollFrequency[line.PayFrequencyTaxOverride]
        : PayrollFrequency[pay.PayFrequency],
      Rate: null,
      PersonId: pay.PersonId,
      Division: division?.Id,
      DivisionName: division?.Name,
      DivisionDisplayId: division?.DisplayId,
      Department: department?.Id,
      DepartmentName: department?.Value,
      DepartmentDisplayId: department?.DisplayId,
      Error: false,
      Warning: false,
      Info: false,
      Message: '', // for tooltip hover
      // for column sorting purposes. 0: nothing, 1: error, 2: warning, 3: informational
      ErrorsAndWarningsSort: 0,
      DetailEdited: rowDetailEdited,
      IsDirty: editedCells.length > 0 || rowDetailEdited,
      PaylineType: line.PaylineType,
      PaylineTypeDisplay: this._getColumnType(line.PaylineType),
      Hidden: this.payrollService.isPaylineHidden(line)
    };

    if (row.OkToPay) {
      this._setRowErrors(line, row);
    } else if (!row.OkToPay && row.PaylineType == PaylineType.Manual) {
      this._setManualPayInfoMessage(row);
    }

    this._buildGridEarningData(row, line, pay);
    this._buildGridDeductionData(row, line, pay);

    return row;
  }

  private _calculatePayline(row) {
    // lock the row to prevent editing until we get a response
    this._lockRowsForCalculations(row.PersonId);
    this.calcPaylineCount++;
    return this.payrollMgmt
      .getEmployeePay(this.clientCode, this.year, this.seqNo, row.PersonId)
      .pipe(
        concatMap((pay: EmployeePay) => {
          return this.payrollMgmt
            .calculatePaylines(this.clientCode, this.year, this.seqNo, pay)
            .pipe(
              tap(resp => {
                this.calcPaylineCount--;
                this._updateRowErrors(pay.PersonId, resp.Paylines);
                this._setChangeRequestErrors(resp);
                resp.Paylines.forEach(payline => {
                  this.payroll = this.payrollMgmt.updatePayline(
                    this.clientCode,
                    this.year,
                    this.seqNo,
                    row.PersonId,
                    payline
                  );
                  if (this.payrollMgmt.hasLaborAllocations(pay, payline)) {
                    row = this.rowData.find(
                      x => x.PersonId == pay.PersonId && x.PaylineId == payline.PaylineId
                    );
                    this._updateRow(payline, row, pay);
                    this._redrawRow(row);
                  }
                });
              }),
              catchError(() => {
                this.calcPaylineCount--;

                const rows = this.rowData.filter(x => x.PersonId == pay.PersonId);

                for (let row of rows) {
                  row.IsLoading = false;
                  row.Error = true;
                  row.Message = 'Unknown error';
                  this._refreshCells(row);
                }

                return of(null);
              })
            );
        })
      );
  }

  // if client is setup for automatic payroll,
  // bounce them to the auto payroll start screen if
  // the payroll type is regular
  // and the automatic worksheet is not skipped for the current pay period
  private _checkAutoPayStatus(locationConfigs: CompanyLocationConfig[]) {
    // if client is setup to auto pay - bounce them to there
    // US 25441 - allow auto payroll clients to run off cycle payroll
    if (
      this.payroll.PayType == PayTypes.Regular &&
      this.payrollMgmt.isClientSetupForAutoPayroll(locationConfigs)
    ) {
      // US 25441 - allow regular payroll when auto payroll has been skipped
      const location = locationConfigs[0];
      this.payrollMgmt
        .isAutoPayrollSkipped(this.clientCode, location.Id, location.Calendars[0].CalendarId)
        .subscribe(skipped => {
          if (!skipped) {
            // automatic payroll has not been skipped, redirect to auto payroll start
            this.router.navigate([`/client/${this.clientCode}/autopayroll`]);
          }
        });
    }
  }

  private _clearChangeRequestErrorsOnPayline(payline: Payline) {
    if (payline.Errors == null || payline.Errors.length == 0) return;
    const changeRequestError = payline.Errors.filter(
      x => x.FieldName != null && x.FieldName.includes('changeRequests')
    );
    if (changeRequestError == null) return;
    for (let i = 0; i < changeRequestError.length, i++; ) {
      const index = payline.Errors.indexOf(changeRequestError[i]);
      if (index > -1) {
        payline.Errors.splice(index, 1);
      }
    }
  }

  // bug 7828
  // if calculate paylines fails, clear loading state
  private _clearLoadingState(employeePay: EmployeePay) {
    if (!employeePay?.Paylines) {
      return;
    }

    for (let payline of employeePay.Paylines) {
      const rowData = this.rowData.find(
        x => x.PersonId == employeePay.PersonId && x.PaylineId == payline.PaylineId
      );

      const node = this.gridApi.getRowNode(rowData.RowId);
      if (node && node.data) {
        node.data.IsLoading = false;
        this._redrawRow(node.data);
      }
    }
  }

  // Bug 7702: comparison function for paylineError objects so we can apply sorting according
  // to the SortFields set in PayrollConfigSetupData.  If we have no sortFields from
  // the base api, default sort is Employee Name then WarningLevel
  private _comparePaylineErrors(left, right) {
    // initialize our sort fields; if we don't get any from config, sort first by
    // name then warning level.  if we DO have sortFields, extend add warningLevel
    // as our last sort
    let sortFields = [1, 5];
    const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);
    if (setupData.SortFields) sortFields = [...setupData.SortFields, 5];

    for (let f of sortFields) {
      let leftField = this._getPaylineErrorField(f, left),
        rightField = this._getPaylineErrorField(f, right);

      if (leftField == rightField) continue;
      else {
        // field == 1 is name comparison; anything else we can just subtract right from left
        if (f == 1) return leftField > rightField ? 1 : -1;
        else return leftField - rightField;
      }
    }

    // if we get to the end of our fields array and everything is equal, return 1
    // to keep left before right
    return 1;
  }

  private _findUncalculatedPaylines(personIds) {
    return this.rowData
      .filter(r => this.paysToCalc.includes(r.PersonId))
      .map(r => {
        if (personIds.includes(r.PersonId)) {
          return of(null);
        }

        personIds.push(r.PersonId);
        return this._calculatePayline(r);
      });
  }

  private _focusRow(personId: number, paylineId: number = null) {
    let row = null;

    // focus on the first payline for this personId
    this.gridApi?.forEachNode(node => {
      // optionally check for paylineId if it was passed
      const paylineCheck = paylineId == null || node.data.PaylineId == paylineId;
      if (node.data.PersonId == personId && row == null && paylineCheck) {
        row = node.rowIndex;
      }
    });

    let cols = this.gridApi.getColumns();

    // focuses the first column (OkToPay)
    this.gridApi.setFocusedCell(row, cols[0]);

    // tab to the first editable input
    this.gridApi.tabToNextCell();
  }

  private _generateDueDateWarnings(startData: PayrollStartData) {
    let date: Date;
    const stringPayDate: any = this.payroll.PayDate;
    if (typeof stringPayDate == 'string') {
      date = new Date(Date.parse(stringPayDate));
    } else {
      date = stringPayDate;
    }
    const location = startData.Locations.find(x => x.Id == this.payroll.LocationId);
    const schedule = location.NextPayDates.find(x =>
      this.payroll.Calendars.map(y => y.CalendarId).includes(x.CalendarId)
    );
    const offset = schedule.RequiredAchPrefundDays * -1;

    this.payrollService
      .isInPrefundWindow(this.clientCode, this.payroll.Year, this.payroll.SequenceNumber)
      .subscribe(isInPrefundWindow => {
        const now = new Date();
        const eod = this.env.endOfDayCutoff;
        const minDate = GetTimeInEST(eod.hours, eod.minutes, isInPrefundWindow.PrefundStartDate);
        if (
          AreDatesEqual(minDate, now) &&
          (now < minDate || schedule.RequiredAchPrefundDays == 0) &&
          !isInPrefundWindow.IsInPrefund
        ) {
          const endOfDay = GetTimeInEST(eod.hours, eod.minutes);
          const eodFormat = endOfDay.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            timeZoneName: 'short',
            hour12: true
          });
          this.dueDateWarning = `Payrolls submitted after 3pm will not process until the next business day.`;
        } else if (now < minDate && !isInPrefundWindow.IsInPrefund) {
          this.dueDateWarning = `This payroll is due to be submitted on ${
            minDate.getMonth() + 1
          }/${minDate.getDate()}/${minDate.getFullYear()} in order for ConnectPay and your Bank to process your payroll in a timely manner.`;
        } else if (now > minDate || isInPrefundWindow.IsInPrefund) {
          this.dueDateWarning = `This payroll is overdue. You can resume this payroll but will need to contact your Payroll Specialist to submit.`;
        }
      });
  }

  private _getColumnType(type: PaylineType) {
    if (type == PaylineType.Manual) {
      return 'NC';
    } else if (type == PaylineType.Void) {
      return 'V';
    } else if (type == PaylineType.NoCheck) {
      return 'NC';
    } else {
      return '';
    }
  }

  private _getEarningAmountSum(line: Payline, earning: CompanyEarningConfig) {
    let sum = 0;
    let count = 0;
    for (const alloc of Object.values(line.Allocations)) {
      if (
        alloc.TemporaryHourlyAllocations != null &&
        alloc.TemporaryHourlyAllocations[earning.Id] != null
      ) {
        count++;
        sum +=
          +alloc.TemporaryHourlyAllocations[earning.Id].TemporaryRateAmount ||
          +alloc.TemporaryHourlyAllocations[earning.Id].PermanentRateAmount;
      }
    }

    return { sum, count };
  }

  private _getEarningHoursSum(line: Payline, earning: CompanyEarningConfig) {
    let placeholder = 0;
    for (const alloc of Object.values(line.Allocations)) {
      if (
        alloc.TemporaryHourlyAllocations != null &&
        alloc.TemporaryHourlyAllocations[earning.Id] != null
      ) {
        placeholder += +alloc.TemporaryHourlyAllocations[earning.Id].Hours;
      }
    }

    return placeholder;
  }

  private _getEditedCells(personId, paylineId) {
    const person = `${personId}`;
    const payline = `${paylineId}`;

    const editedCells = this.sessionData.ChangedColumns;

    if (!Object.keys(editedCells).includes(person)) {
      editedCells[person] = {};
    }

    if (!Object.keys(editedCells[person]).includes(payline)) {
      editedCells[person][payline] = [];
    }

    return editedCells[person][payline];
  }

  private _getEligiblePaylineVoids(resp: MasterPayrollObject[]) {
    // BUG 26801 - filter response to exclude any void paylines already added to this payroll
    // loop through each payline in this payroll & find any that are pending voids
    const eligibleVoids = [];
    const paylines = this.payroll.EmployeePays.flatMap(emp =>
      emp.Paylines.filter(payline => payline.HistoryVoidData != null)
    );

    const pendingVoids = paylines.map(x => x.HistoryVoidData);

    if (!pendingVoids.length) {
      return resp;
    }

    for (let payroll of resp) {
      const pending = pendingVoids.filter(
        x => payroll.Year == x.PayrollYear && payroll.SequenceNumber == x.PayrollSeq
      );

      if (pending?.length) {
        // check to see which paylines in this payroll are already pending void

        for (let p of pending) {
          payroll.EmployeePays[0].Paylines = payroll.EmployeePays[0].Paylines.filter(
            x => x.PaylineId != p.PaylineId
          );
        }

        // if any paylines still exist after filter, add them to the eligible voids
        if (payroll.EmployeePays[0].Paylines.length) {
          eligibleVoids.push(payroll);
        }
      } else {
        eligibleVoids.push(payroll);
      }
    }

    return eligibleVoids;
  }

  private _getEmployeeDepartment(departmentId: number): KeyTriple {
    let department: KeyTriple = null;
    if (this.divisions && this.divisions.length && departmentId) {
      this.divisions.forEach(div => {
        if (div.Departments && div.Departments.length) {
          div.Departments.forEach(dept => {
            if (dept.Id == departmentId) {
              department = dept;
            }
          });
        }
      });
    }

    return department;
  }

  private _getEmployeeDivision(divisionId: number): Division {
    let division: Division = null;
    if (this.divisions && this.divisions.length && divisionId) {
      this.divisions.forEach(div => {
        if (div.Id == divisionId) {
          division = div;
        }
      });
    }

    return division;
  }

  private _gridDataReady() {
    // NOTE: if gridoptions.api/columnApi are undefined,
    // check ngIf logic on grid to make sure it has rendered
    this.isLoading = false;

    this._autoSizeGridHeight();

    setTimeout(() => {
      this._autoSizeAllColumns();
      this._setColumnVizIndicator();

      if (this.selectedEmployee != null) {
        this._focusRow(this.selectedEmployee);
      }
    }, 100);
  }

  private _getGridOptions(): GridOptions {
    const gridOptions: GridOptions = {
      animateRows: false,
      enterNavigatesVertically: true,
      enterNavigatesVerticallyAfterEdit: true,
      // set the node id to our synthetic id generated in _buildRow
      getRowId: params => params.data.RowId,
      onColumnMoved: (e: ColumnMovedEvent) => {
        if (e.finished) {
          // column move event finished
          this.auditService.audit('payroll-column-moved', {
            ClientCode: this.clientCode,
            Year: this.payroll.Year,
            SequenceNumber: this.payroll.SequenceNumber,
            Column: e.column.getColId()
          });
        }
      },
      onGridReady: e => {
        this.gridApi = e.api;
        setTimeout(() => {
          this.calculateTotalsInView();
          this._setChangeRequestErrors();
          const sortByLast = this.userPrefSvc.getPayrollEmployeeSortByLastName(
            this.clientCode,
            this.userPreferences
          );

          if (sortByLast != null) {
            this._handleEmployeeSortPrefChange(this.userPreferences);
          }
        }, 500);
      },
      onBodyScroll: (e: BodyScrollEvent) => {
        // bug 15049: do not fire grid scroll event
        // (which fires calculated lines in view function)
        // on horizontal scroll. this causes issues
        // when users tab across the grid and start scrolling
        // horizontally
        if (e.direction == 'horizontal') {
          return;
        }

        this._onGridScroll();

        // need to close the 'Action' drop down
        // on vertical scroll, or it will continue
        // to be visible even when the row is overflow hidden
        const focusedCell = this.gridApi.getFocusedCell();

        if (!focusedCell) {
          return;
        }

        const rowNode = this.gridApi.getDisplayedRowAtIndex(focusedCell.rowIndex);

        // if the focused cell is 'Actions', or the payline has been 'detailed',
        // close any dropdowns that may be open
        if (focusedCell.column.getColDef().field == 'MoreActions' || rowNode.data.DetailEdited) {
          this.sharedService.closeMoreActionsPopup.next();
        }
      },
      onCellEditingStarted: params => {
        // when cell edit starts, update our "currentRow" tracker
        // if currentRow has changed, inspect previous row
        // if previousRow needsCalc, get totals, update the payroll, and write to db
        // NOTE: due to the custom OkToPay checkbox, toggling that
        // field does NOT trigger this event
        this.isGridInEditMode = true;
        if (this.currentRow) {
          this._updateRowTrackerAndCalculate(
            params.data,
            params.api.getRowNode(this.currentRow.RowId),
            params.column.getColId()
          );
        } else {
          this.currentRow = params.data;
          this.currentColId = params.column.getColId();
        }
      },
      onCellEditingStopped: params => {
        this.isGridInEditMode = false;
      },
      onCellFocused: params => {
        // if the user is tabbing or arrow key navigating around
        // the grid, the "more actions" drop down should be closed.
        // FIXME/NOTE: this could be further optimized by only firing it
        // when it is known the drop down is open, or the user
        // was at least focused on the Actions cell or a row where
        // DetailEdited == true.
        this.sharedService.closeMoreActionsPopup.next();

        const row = params.api.getDisplayedRowAtIndex(params.rowIndex);

        // if a group is collapsed, this may fire with row being undefined
        if (!row) {
          return;
        }

        // NOTE: the logic below is intended to start editing the cell immediately
        // as it gains "focus" (user clicks on it or tabs, arrow key navigates there)
        // if the cell is editable and the user has changed rows or columns, start editing the cell.
        // by default ag-grid starts editing when the user presses a key on the keyboard, but does not
        // register that first key event as a value change in the cell.
        // When CSRs are quickly entering in payrolls, they cannot feel like any of their keystrokes are lost.

        // EXTRA NOTE: the edge case scenario is pressing enter on the last row
        // will result in the current cell no longer being editable but still focused.
        // trap the scenario of being on the cell and gaining focus on the cell that
        // is already the current row and current column.
        const isLastRow = params.rowIndex == this.gridApi.getLastDisplayedRowIndex();

        // see fixme @ 6e59641f for issue with Enter key press firing multiple times
        // as well as Bug #24047
        const colId = this.gridApi.getColumn(params.column).getColId();
        if (
          this.gridApi.getColumn(params.column).isCellEditable(row) &&
          (row.id != this.currentRow?.RowId || colId != this.currentColId || isLastRow)
        ) {
          this._startEditingCell(params.rowIndex, params.column);
        } else if (this.currentRow) {
          this._updateRowTrackerAndCalculate(
            row.data,
            params.api.getRowNode(this.currentRow.RowId),
            colId
          );
        }
      },
      onCellValueChanged: params => {
        // NOTE: onCellValueChanged is responsible for (in order)
        // *) if a cell is cleared, set the value to 0. bug 8251
        // *) update paygrid session data so edited cells have a darker border
        // *) check if the OkToPay should be toggled if payline has non-zero values in it
        // *) apply row data changes back to the in memory payroll object
        // *) update the OkToPay count to enable/disable review & submit button
        // *) if the changed field is OkToPay, update row tracker

        // if oldVal is undefined and newVal is null, or vice versa, ignore
        if (!params.oldValue && !params.newValue) return;

        this.auditService.auditPayroll(`payroll-payline-edited-from-grid`, {
          ...this.payrollMgmt.getAuditData(this.payroll)
        });
        // first confirm we actually have a change
        if (params.oldValue != params.newValue) {
          // Bug 8251: when clearing an input,
          // set the value to 0 rather than empty string
          // so the Base can determine if the TemporaryRate/Hours
          // were never set or explicity cleared by the user
          if (!params.newValue && params.oldValue && params.colDef.field != 'OkToPay') {
            params.data[params.colDef.field] = 0;
          }

          const editedCells = this._getEditedCells(params.data.PersonId, params.data.PaylineId);
          editedCells.push(params.colDef.field);

          // OkToPay auto check logic
          // if any field is changed other than the OkToPay flag
          // check if any cell values are non-zero
          // if so, OkToPay is set to true, otherwise it is false
          if (params.colDef.field != 'OkToPay') {
            const okToPay = this._rowHasNonZeroValue(params.data);

            if (params.data.OkToPay != okToPay) {
              // the ok to pay flag has been checked or unchecked for the user
              params.data.OkToPay = okToPay;
              params.api.applyTransaction({ update: [params.data] });
            }
          }

          // apply our row data change to the underlying payroll, then save the paygrid
          // get the employeePay and payline that match this row
          let empPay = this.payroll.EmployeePays.find(p => p.PersonId == params.data.PersonId);
          let payline = empPay.Paylines.find(l => l.PaylineId == params.data.PaylineId);

          this._updatePayline(payline, params.data, true);

          // update the OkToPay count to enable/disable review & submit button
          this._updateOkToPayCount();

          // if this is the first row being edited, set the current row id
          // otherwise, let onCellEditingStarted or OkToPay logic handle it
          if (this.currentRow == null) {
            this.currentRow = params.data;
          } else if (params.colDef.field == 'OkToPay') {
            // NOTE: due to the custom OkToPay checkbox, toggling it
            // does not trigger the onCellEditingStarted, which handles
            // setting the current row
            this._updateRowTrackerAndCalculate(
              params.data,
              params.api.getRowNode(this.currentRow.RowId),
              params.column.getColId()
            );

            this.currentRow = params.data;
          }
        }
      },
      processUnpinnedColumns: (params: ProcessUnpinnedColumnsParams<any>) => {
        const lockPinnedCols = ['Name', 'OkToPay'];

        params.columns = params.columns.filter(x => !lockPinnedCols.includes(x.getColDef().field));

        return params.columns;
      },
      isExternalFilterPresent: () => true,
      doesExternalFilterPass: params => !params.data.Hidden,

      // NOTE: this is turned on in order to auto size all columns
      // on grid ready, not just the ones visible.
      // if column virtualization is needed, a solution for that will need
      // to be found
      suppressColumnVirtualisation: true,
      // bug 10109 - property name like "OTH.ER" will break the deep referencing when the name contains a "."
      suppressFieldDotNotation: true
    };
    return gridOptions;
  }

  // Bug 7702: getter function to retrieve property values from PayLineError
  // models, kept in this.paylineErrors.  Extends our usual sort fields with
  // the addition of '5' for retrieving a message's WarningLevel
  private _getPaylineErrorField(fieldId, error) {
    switch (fieldId) {
      case 1:
        return `${error.LastName} ${error.FirstName}`;
      case 2:
        return error.Id;
      case 3:
        return error.DivisionId;
      case 4:
        return error.DepartmentId;
      case 5:
        return error.WarningLevel;
    }
  }

  private _getPayroll(locationConfigs: CompanyLocationConfig[]) {
    this.payrollMgmt
      .getPayroll(this.clientCode, this.year, this.seqNo)
      .pipe(
        tap(payroll => {
          this.payroll = payroll;
          this._validatePayroll(payroll);

          const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);
          const sortByLast = this.userPrefSvc.getPayrollEmployeeSortByLastName(
            this.clientCode,
            this.userPreferences
          );
          this.payrollMgmt.sortEmployeePays(this.payroll.EmployeePays, setupData, sortByLast);
          this.payrollMgmt.sortPaylinesBySeq(this.payroll);

          this._getTaxConfigs(payroll);

          this.freqDisplay = this.payrollMgmt.payrollFrequencyDisplay(
            this.payroll.EmployeePays,
            false
          );

          this.sessionData = this.payrollMgmt.getSessionData(
            this.clientCode,
            this.year,
            this.seqNo
          );
        })
      )
      .subscribe(() => {
        this.payrollService.getPayrollStartData(this.clientCode).subscribe(startData => {
          this.startData = startData;
          if (this.authService.isInAnyRole(['CSR1', 'RunPayroll', 'Client Portal Admin'])) {
            this._generateDueDateWarnings(startData);
          }
        });

        this._checkAutoPayStatus(locationConfigs);
        this._initGrid();
        this.render = true;
      });
  }

  private _getTaxConfigs(payroll: MasterPayrollObject) {
    // task 8963 - fetch the company tax configs
    this.companyService.getTaxConfigs(this.clientCode, false).subscribe(taxConfigs => {
      // loop through all payline > taxes for all employee pay
      // build a map of tax keyed off taxCode
      payroll.EmployeePays.forEach(pay => {
        pay.Paylines.forEach(line => {
          line.Taxes.forEach(tax => {
            this.taxConfigs[tax.TaxCode] = taxConfigs.find(
              tc => tc.TaxCode.trim() == tax.TaxCode.trim()
            );
          });
        });
      });
    });
  }

  private _getUpdatedEmployeePay() {
    const peopleSub = this.peopleService.getPerson(this.clientCode, this.selPersonId);
    // fetch the updated paylines from the API
    const payrollSub = this.payrollService.getEmployeePayPaylines(
      this.clientCode,
      this.year,
      this.seqNo,
      this.selPersonId
    );

    forkJoin([peopleSub, payrollSub]).subscribe(val => {
      const paylines = val[1];
      const person = val[0];
      // reload the grid with new data.
      this.selEmployeePay.Paylines = paylines.Model;
      // update row data for paylines we get back in the response
      const name = `${person.FirstName || ''} ${person.MiddleName} ${person.LastName || ''}`.trim();
      this.selEmployeePay.EmployeeFirstName = person.FirstName;
      this.selEmployeePay.EmployeeMiddleName = person.MiddleName;
      this.selEmployeePay.EmployeeLastName = person.LastName;
      this.selEmployeePay.EmployeeNumber = person.EmployeeId;
      this.selEmployeePay.DepartmentId = person.Department;
      this.selEmployeePay.DivisionId = person.Division;
      this.selEmployeePay.PayFrequency = person.PayFrequency;

      for (let line of paylines.Model) {
        let rowData = this.rowData.find(
          x => x.PaylineId == line.PaylineId && x.PersonId == this.selPersonId
        );

        rowData.Department = person.Department;
        rowData.Division = person.Division;
        rowData.EmployeeNumber = person.EmployeeId;
        rowData.Frequency = PayrollFrequency[person.PayFrequency];
        rowData.Name = name;

        // reset the initial states of the paylines for the 'Restore Defaults' functionality
        this.payrollService
          .setInitialPaylineState(
            this.clientCode,
            this.year,
            this.seqNo,
            this.payroll.Status,
            this.selPersonId,
            line
          )
          .subscribe(() => {});

        // bug 16344
        // clear presets if earnings/deductions/etc were held
        this.payrollMgmt.clearPresets(line, this.payroll, this.selEmployeePay);

        // update row data so grid is accurate
        this._updateRow(line, rowData, this.selEmployeePay);
      }

      // update underlying payline data in the payroll mgmt cache
      this.payrollMgmt.updateEmployeePay(
        this.clientCode,
        this.year,
        this.seqNo,
        this.selEmployeePay
      );

      // multiple paylines are removed after a person is edited in payroll
      // strip those paylines out of the grid's row data here
      const rows = this.rowData.filter(x => x.PersonId == this.selPersonId);
      for (let row of rows) {
        const line = this.selEmployeePay.Paylines.find(x => x.PaylineId == row.PaylineId);

        const index = this.rowData.indexOf(row);

        if (!line) {
          this._removeEmployeeFromGrid(row.PaylineId);
        } else if (index > -1) {
          // bug 20571
          // rebuild the row. if deductions or earnings have been disabled,
          // those entried need to removed from rowData so the grid cell is disabled

          // NOTE: call grid api to udate the row model
          this.rowData[index] = this._buildRow(line, this.selEmployeePay);
          this.gridApi.setGridOption('rowData', this.rowData);
          this._refreshCells(this.rowData[index]);
        }
      }

      this._updateRowErrors(this.selPersonId, paylines.Model);

      this.allRowData = [...this.rowData];
      this.rowFilterIndicator = `Displaying ${
        this.allRowData.filter(r => !r.Hidden).length
      } total rows`;

      // focus the payline
      this._focusRow(this.selPersonId);
      this.showPersonEditor = false;
    });
  }

  private _handleArrowKeyPress(code: string) {
    let c = this.gridApi.getFocusedCell();

    if (!c) {
      return;
    }

    // if the grid is not in edit mode, do nothing. in render mode,
    // arrow key navigation works on the grid by default.
    if (!this.isGridInEditMode) {
      return;
    }

    if (code === 'ArrowUp' && c.rowIndex > 0) {
      this.gridApi.stopEditing();
      this.gridApi.setFocusedCell(c.rowIndex - 1, c.column);
    } else if (code === 'ArrowDown' && c.rowIndex + 1 < this.allRowData.length) {
      this.gridApi.stopEditing();
      this.gridApi.setFocusedCell(c.rowIndex + 1, c.column);
    } else if (code === 'ArrowLeft') {
      const moved = this.gridApi.tabToPreviousCell();
      if (!moved) {
        // user hit the first cell, bring focus back
        this._bringFocusBack();
      }
    } else if (code === 'ArrowRight') {
      const moved = this.gridApi.tabToNextCell();
      if (!moved) {
        // user hit the last cell, bring focus back
        this._bringFocusBack();
      }
    }
  }

  private _handleEmployeeSortPrefChange(userPreferences: UserPreferences) {
    this.userPreferences = userPreferences;

    // get the active sort state of the Name column
    const sortState = this.gridApi.getColumnState().find(x => x.colId == 'Name').sort;

    const sortByLast = this.userPrefSvc.getPayrollEmployeeSortByLastName(
      this.clientCode,
      userPreferences
    );

    if (sortState && sortByLast == null) {
      // user was actively sorting on the Name column, then picked `Sort by default`
      // clear the active sorting on the Name column
      this.gridApi.applyColumnState({
        state: [{ colId: 'Name', sort: null }]
      });
    } else if (sortState && sortByLast != null) {
      // when the sort by first/last name option changes and the column is actively sorted,
      // the column needs to be resorted (comparator function needs to fire again).
      // the only way to re-fire the comparator function is to change the sort state from it's
      // current value. call this twice as a hacky way to get around that issue.
      this.gridApi.applyColumnState({
        state: [{ colId: 'Name', sort: sortState == 'asc' ? 'desc' : 'asc' }]
      });

      this.gridApi.applyColumnState({
        state: [{ colId: 'Name', sort: sortState }]
      });
    } else if (!sortState && sortByLast != null) {
      // when the user picks sort by First or Last Name
      // start to actively sort on the Name column
      this.gridApi.applyColumnState({
        state: [{ colId: 'Name', sort: 'asc' }]
      });
    }
  }

  // return true if user toggled the column visibility to be hidden
  // return null if nothing is set
  private _hideColFromPrefs(colName: string) {
    const visibility = this.userPrefSvc.getPayrollColumnVisibility(
      this.clientCode,
      this.userPreferences
    );

    if (!visibility) {
      return null;
    }

    const column = visibility.find(x => x.Key == colName);

    // preferences capture true/false to dictate display
    // invert that to return if column should be hidden
    return !column ? null : !column.Value;
  }

  private _initGrid() {
    const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);
    if (this.areLaborAllocationsEnabled()) {
      this.laborAllocService.init(this.client.LaborAllocationType, setupData, this.jobCodes);
    }

    // task 7466 - check for client division/department
    // to determine if those columns should be rendered
    if (setupData.HasDivisions || setupData.HasDepartments) {
      this.companyService.getDivisions(this.clientCode).subscribe(divisions => {
        this.divisions = divisions;
        this._buildGrid();
      });
    } else {
      this._buildGrid();
    }

    this._updateOkToPayCount();
  }

  // given the payline row that needs to be re-calculated, prep the UI for calcultaions,
  // then issue the getTotals request.  when we get a response, apply totals or errors
  // to our payroll model and persist changes to the database
  private _initiatePaylineCalculation(rowData) {
    let responseObserver = {
      next: (resp: EmployeePay) => {
        if (!resp || !resp.Paylines) {
          return;
        }

        // task 9324 - if multiple paylines, display notification
        if (resp?.Paylines?.length > 1) {
          this.toastService.show(
            `All pays for ${this.payrollMgmt.getEmployeeFullName(resp)} have been recalculated`
          );
        }

        resp.Paylines.forEach(l => this._setPaylineCalculated(l, resp));

        this.payrollMgmt.updatePayGridSessionData(
          this.clientCode,
          this.year,
          this.seqNo,
          this.sessionData
        );
      },
      error: (resp: EmployeePay) => {
        this._clearLoadingState(resp);
      },
      complete: () => {
        this._processCalcPaylineRequests();
      }
    };

    // // task 25407
    // // if there happens to be another request in the queue already for this person,
    // // remove it from the queue. this latest request will contain the most recent changes on the grid
    this.calcPaylineQueue = this.calcPaylineQueue.filter(
      x => x.rowData.PersonId != rowData.PersonId
    );

    this.calcPaylineQueue.push({ rowData, callback: responseObserver });

    this._processCalcPaylineRequests();
  }

  private _isEarningCellEnabled(
    params: EditableCallbackParams | ICellRendererParams,
    line: Payline,
    config: CompanyEarningConfig
  ) {
    return (
      config &&
      Object.keys(params.data).includes(params.colDef.field) &&
      !params.data.IsLoading &&
      !params.data.DetailEdited &&
      // bug 26664 - disable any non regular payline type
      params.data.PaylineType == PaylineType.Normal &&
      this.payrollMgmt.isEarningEditable(line, config.Id)
    );
  }

  private _isDeductionCellEnabled(params: EditableCallbackParams) {
    return (
      Object.keys(params.data).includes(params.colDef.field) &&
      !params.data.IsLoading &&
      !params.data.DetailEdited &&
      // bug 26664 - disable any non regular payline type
      params.data.PaylineType == PaylineType.Normal
    );
  }

  private _listenForSharedServices() {
    this.addPaylineSub = this.sharedService.addPayline.subscribe(args =>
      this._onAddPaylineEvent(args)
    );

    this.addPersonSub = this.sharedService.addPersonToPayroll.subscribe(pay =>
      this._addPersonToPayroll(pay)
    );

    this.sharedService.focusPayline
      .pipe(delay(100))
      .subscribe(personId => this._focusRow(personId));

    this.openDetailsSub = this.sharedService.openPaylineDetails.subscribe(
      (args: OpenPaylineDetailsArgs) => this._openPaylineDetails(args)
    );

    this.openAddPersonSub = this.sharedService.openAddPersonToPayrollModal.subscribe(() => {
      const personIds = [];

      // calculate anything that is uncalculated.
      // this helps prevent lost data when user changes payline data and clicks
      // the 'add person' link before navigating to another payline
      let paysToCalc = this._findUncalculatedPaylines(personIds);

      if (paysToCalc.length) {
        forkJoin(paysToCalc).subscribe(
          () => {
            this.isLoading = false;
            this._setPaylinesCalculated(personIds);
          },
          err => {
            this.isLoading = false;
          }
        );
      }

      // task 9742
      // when the add person modal opens, set the current payroll info
      // in the shared service, so the create person editor
      // can have the information it needs if it has to put the payroll
      // status on hold.
      this.sharedService.setPayrollStatusInfo(this.payroll);
    });

    this.openPersonEditorSub = this.sharedService.openPayrollPersonModal.subscribe(personId => {
      this.selPersonId = personId;
      this.selEmployeePay = this.payroll.EmployeePays.find(x => x.PersonId == personId);
      this.showPersonEditor = true;
    });
  }

  private _lockRowsForCalculations(personId) {
    // show spinner & disable any paylines for this employee
    const rows = this.rowData.filter(x => x.PersonId == personId);

    for (let row of rows) {
      if (!row.IsLoading) {
        row.IsLoading = true;
        this._refreshCells(row); // sets disabled input styling

        // clear any previous errors/warnings/messages
        row.Error = false;
        row.Warning = false;
        row.Info = false;
        row.Message = '';
        row.ErrorsAndWarningsSort = 0;
      }
    }
  }

  private _openAddPaylineModal(
    args: SelectedPaylineArgs,
    completedPayrolls: MasterPayrollObject[]
  ) {
    const modalRef = this.ngModalService.open(AddPaylineTypeComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'centered add-payline-modal'
    });

    const c = modalRef.componentInstance as AddPaylineTypeComponent;

    c.deductionConfigs = this.deductionConfigs;
    c.earningConfigs = this.earningConfigs;
    c.historyVoidPayrolls = completedPayrolls;
    c.selectedPaylineArgs = args;
    c.payroll = this.payroll;
    c.client = this.client;

    c.onAddHistoryVoid = (args: OpenPaylineDetailsArgs) => {
      this._addHistoryVoidPayline(args, c.selHistoryVoid);
      modalRef.close();
    };

    c.onAddRegularPay = () => {
      this._addRegularPayline(args);
      modalRef.close();
    };

    c.onAddNoCheck = (empPay: EmployeePay, payline: Payline) => {
      const emp = this.payroll.EmployeePays.find(x => x.PersonId == args.PersonId);
      const index = this.payroll.EmployeePays.indexOf(emp);

      this.payroll.EmployeePays[index] = empPay;

      this._addPaylineToGrid(empPay, payline);

      this.auditService.auditPayroll(`payroll-payline-added`, {
        ...this.payrollMgmt.getAuditData(this.payroll),
        PaylineId: payline.PaylineId
      });

      this.closePaylineDetails({ pay: empPay, line: payline, isDirty: true });

      if (payline.UserConfirmedNonRegularPayline) {
        modalRef.close();
      }
    };

    c.onClose = () => modalRef.close();
    c.onDismiss = () => modalRef.dismiss();
    c.startData = this.startData;
    c.taxConfigs = this.taxConfigs;
  }

  private _onAddPaylineEvent(args: SelectedPaylineArgs) {
    // bug 9797.
    // if the working payline needs to be calculated,
    // do that before adding a new payline.
    // the API returns a collection of paylines in the addNewPayline response.
    // the current payline's values will not be up to date if not calculated first.
    if (this.currentRow && this.paysToCalc.includes(args.PersonId)) {
      const node = this.gridApi.getRowNode(this.currentRow.RowId);

      this._calculatePayline(node.data).subscribe(resp => {
        this._addPayline(args);
      });
    } else {
      // there was no current working row or row did not need calc. add payline
      this._addPayline(args);
    }
  }

  private _onGridScroll() {
    this.calculateTotalsInView();
  }

  private _openPaylineDetails(args: OpenPaylineDetailsArgs): void {
    if (this.showPaylineDetails) {
      return;
    }

    // the selected employee pay to pass to the detail editor
    this.payrollMgmt
      .getEmployeePay(this.clientCode, this.year, this.seqNo, args.PersonId)
      .subscribe(pay => {
        // this JSON stringify/parse is seemingly the best approach
        // to deep copy an object with nested properties.
        this.selEmployeePay = JSON.parse(JSON.stringify(pay));

        const line = pay.Paylines.find(x => x.PaylineId == args.PaylineId);

        this.paylineDetailArgs = args;

        // if the payline was edited on the grid or in the detail editor
        const editedCells = this._getEditedCells(pay.PersonId, line.PaylineId);

        const detailEdited = this.sessionData.DetailedRowIds?.includes(
          `person-${pay.PersonId}-payline-${line.PaylineId}`
        );

        if (!this.sessionData.DetailedRowIds) {
          this.sessionData.DetailedRowIds = [];
        }

        this.sessionData.DetailedRowIds.push(`person-${pay.PersonId}-payline-${line.PaylineId}`);
        this.payrollMgmt.updatePayGridSessionData(
          this.clientCode,
          this.year,
          this.seqNo,
          this.sessionData
        );

        this.paylineDetailArgs.IsDirty = editedCells.length > 0 || detailEdited;

        // set initial payline state for 'restore defaults' button
        this.payrollService
          .getInitialPayrollState(this.clientCode, this.year, this.seqNo)
          .subscribe(resp => {
            this.showPaylineDetails = true;

            const initialPay = resp.EmployeePays.find(x => x.PersonId == args.PersonId);

            this.initialPaylineState = initialPay.Paylines.find(x => x.PaylineId == args.PaylineId);

            // audit the payline detail open event
            this.auditService.auditPayroll(`payroll-payline-detail-opened`, {
              ...this.payrollMgmt.getAuditData(this.payroll),
              PaylineId: args.PaylineId,
              PersonId: args.PersonId
            });
          });
      });
  }

  // in an effort to make the grid for performant, cap the number of simultaneous calc payline requests
  private _processCalcPaylineRequests() {
    if (!this.calcPaylineQueue?.length) return;

    // limit has not been reached, send calc payline request with next in queue
    if (this.calcPaylineCount < this.calcPaylineLimit) {
      const req = this.calcPaylineQueue[0];
      this._calculatePayline(req.rowData).subscribe(req.callback);
      this.calcPaylineQueue.splice(0, 1);
    }
  }

  private _redrawRow(row) {
    // redraw rows before and after validation
    // to ensure inputs disabled/enabled styling is set
    // also ensures the errors/warning tooltip hover works
    let node = this.gridApi.getRowNode(row.RowId);

    this.gridApi.redrawRows({
      rowNodes: [node]
    });
  }

  private _refreshCells(row = null) {
    if (row) {
      const node = this.gridApi.getRowNode(row.RowId);
      if (node) {
        this.gridApi.refreshCells({
          rowNodes: [node],
          force: true
        });
      }
    } else {
      this.gridApi.refreshCells({
        force: true,
        suppressFlash: true
      });
    }
  }

  private _removeEmployeeFromGrid(paylineId: number = null) {
    // if a payline id is passed, just remove that payline
    // else remove all payline for this employee
    if (paylineId) {
      var pays = this.payroll.EmployeePays.find(x => x.PersonId != this.selPersonId);
      pays.Paylines = pays.Paylines.filter(x => x.PaylineId != paylineId);

      this.rowData = this.rowData.filter(
        x => !(x.PersonId == this.selPersonId && x.PaylineId == paylineId)
      );
    } else {
      this.payroll.EmployeePays = this.payroll.EmployeePays.filter(
        x => x.PersonId != this.selPersonId
      );
      this.rowData = this.rowData.filter(x => x.PersonId != this.selPersonId);
    }

    this.allRowData = [...this.rowData];
    this.rowFilterIndicator = `Displaying ${this.allRowData.length} total rows`;

    this._refreshCells();
  }

  private _rowHasNonZeroValue(rowData): boolean {
    // loop through all row data to see if we have any non-zero
    // earnings or deductions data
    const setupData = this.payrollMgmt.getSetupData(this.clientCode, this.year, this.seqNo);
    for (let i = 0; i < Object.keys(rowData).length; i++) {
      let value = Object.values(rowData)[i];
      let key = Object.keys(rowData)[i];

      // loop through available earnings
      for (let j = 0; j < setupData.Earnings.length; j++) {
        let earning = setupData.Earnings[j];

        // earnings are duplicated if hours and rates can both be set
        // suffixed with 'Hours' or 'Rate'
        if (
          (earning.Name == key || `${earning.Name}Hours` == key || `${earning.Name}Rate` == key) &&
          +value
        ) {
          // got a non-zero value; we're OK so bail out
          return true;
        }
      }

      // loop through available deductions
      for (let k = 0; k < setupData.Deductions.length; k++) {
        let deduction = setupData.Deductions[k];

        // NOTE: update this logic if/when deduction columns
        // get duplicates for Hours/Rate like earnings are
        if (deduction.Name == key && +value) {
          // got a non-zero value; we're OK so bail out
          return true;
        }
      }
    }

    var rateData = rowData.RateData as RowRateData;
    if (rateData.Type == RateType.Salary) {
      var pays = this.payroll.EmployeePays.find(x => x.PersonId == rowData.PersonId);
      var payline = pays.Paylines.find(x => x.PaylineId != rowData.paylineId);

      var salaryEarning = payline.Earnings.find(e => e.EarningId == rateData.SalaryId);
      if (+salaryEarning.TemporaryRateAmount || rowData.Rate != salaryEarning.PermanentRateAmount) {
        return true;
      }
    }

    // checked everything and didn't find any non-zero vals;
    return false;
  }

  private _setCellRendererParams(params, cellParams) {
    // the cell render parameters can change after the user
    // has visited the payline detail editor. if the value
    // needs to be obscured to the user on the grid,
    // { mode: 'hidden'} is passed to the cell renderer.
    if (!params.data.DetailEdited) {
      return { ...cellParams };
    }

    // jump through hoops to fetch the deduction and config
    const dedName = params.colDef.field;
    const config = Object.values(this.deductionConfigs).find(c => c.Name == dedName);

    // this is either not a deduction column
    // or we do not have the company deduction config
    if (!config) {
      return { ...cellParams };
    }

    let deduction;

    // find the matching payline and deduction
    // in order to check the TemporaryAmount
    for (let pay of this.payroll.EmployeePays) {
      if (pay.PersonId != params.data.PersonId) {
        continue;
      }

      for (let line of pay.Paylines) {
        if (line.PaylineId != params.data.PaylineId) {
          continue;
        }

        for (let ded of line.Deductions) {
          if (ded.DeductionCode == config.Id) {
            deduction = ded;
            break;
          }
        }
      }
    }

    if (!deduction) {
      return { ...cellParams };
    }

    // For Task 8941, if calc method is not fixed
    // and deduction has TemporaryAmount, obscure the cell value.
    if (
      config.CalculationMethod != DeductionCalculations.Fixed &&
      deduction.TemporaryAmount != null
    ) {
      const params = { ...cellParams };
      params.mode = 'hidden';
      return params;
    } else {
      return { ...cellParams };
    }
  }

  private _setCellStyle(params, defaultStyle = {}) {
    const editedCells = this._getEditedCells(params.data.PersonId, params.data.PaylineId);
    if (editedCells && editedCells.includes(params.colDef.field)) {
      return {
        ...defaultStyle,
        border: '1px solid rgb(163, 163, 163)',
        'border-radius': '3px'
      };
    } else {
      return { ...defaultStyle };
    }
  }

  private _setChangeRequestErrors(pay: EmployeePay | null = null) {
    this.store.selectOnce(ClientSelectionState.getOpenChangeRequests).subscribe(changeRequests => {
      if (changeRequests.length > 0) {
        if (pay == null) {
          this.payroll.EmployeePays.forEach(employeePay => {
            this._setChangeRequestErrorsOnPayline(changeRequests, employeePay);
            this._updateRowErrors(employeePay.PersonId, employeePay.Paylines);
          });
        } else {
          this._setChangeRequestErrorsOnPayline(changeRequests, pay);
          this._updateRowErrors(pay.PersonId, pay.Paylines);
        }
      } else {
        //We might need to clear existing errors.
        if (pay == null) {
          this.payroll.EmployeePays.forEach(employeePay => {
            employeePay.Paylines.forEach(payline => {
              this._clearChangeRequestErrorsOnPayline(payline);
            });
            this._updateRowErrors(employeePay.PersonId, employeePay.Paylines);
          });
        } else {
          pay.Paylines.forEach(payline => {
            this._clearChangeRequestErrorsOnPayline(payline);
          });
          this._updateRowErrors(pay.PersonId, pay.Paylines);
        }
      }
    });
  }

  private _setChangeRequestErrorsOnPayline(
    changeRequests: ChangeRequestHistoryEntry[],
    pay: EmployeePay
  ) {
    const requests = changeRequests.filter(x => x.PersonId == pay.PersonId);
    if (requests != null && requests.length > 0) {
      const hasExistingError = pay.Paylines.some(x => {
        if (x.Errors == null || x.Errors.length == 0) {
          return false;
        }
        return x.Errors.some(y => y.Message.includes('employee change request'));
      });
      if (!hasExistingError) {
        pay.Paylines.forEach(payline => {
          if (payline.Errors == null) {
            payline.Errors = [];
          }
          requests.forEach(request => {
            payline.Errors.push({
              FieldName: 'changeRequests',
              Message: `This person has a pending ${request.RequestType} request.`,
              WarningLevel: WarningLevel.Warning
            });
          });
        });
      }
    }
  }

  private _setColVizRequest() {
    const v = this.userPreferences.ClientPrefs[this.clientCode].Payroll.ColumnVisibility;
    this.userPrefSvc.setPayrollColumnVisibility(this.clientCode, v).subscribe();
  }

  private _setManualPayInfoMessage(row) {
    row.Info = true;
    row.Message = `This Manual Check pay for ${row.Name} will be discarded when payroll is approved.`;
    row.ErrorsAndWarningsSort = 3;
  }

  // update the paygrid session data
  // to track which paylines have been calculated since payroll was created
  private _setPaylineCalculated(payline: Payline, pay: EmployeePay) {
    if (!Object.keys(this.sessionData.CalculatedPaylines).includes(`${pay.PersonId}`)) {
      this.sessionData.CalculatedPaylines[pay.PersonId] = [];
    }

    if (!this.sessionData.CalculatedPaylines[pay.PersonId].includes(payline.PaylineId)) {
      this.sessionData.CalculatedPaylines[pay.PersonId].push(payline.PaylineId);
    }
  }

  private _setPaylinesCalculated(personIds: Array<number>, setPayrollModified = true) {
    // store in session data that these paylines have been calculated
    // in order to properly track when the holds/overrides logic should
    // be run on payroll init
    for (let id of personIds) {
      const pay = this.payroll.EmployeePays.find(x => x.PersonId == id);
      for (let line of pay.Paylines) {
        this._setPaylineCalculated(line, pay);
      }
    }
    this.payrollMgmt.updatePayGridSessionData(
      this.clientCode,
      this.year,
      this.seqNo,
      this.sessionData,
      setPayrollModified
    );
  }

  private _setRowErrors(payline: Payline, row) {
    if (payline.Errors && payline.Errors.length) {
      // if there are any critical errors, display critical error icon in row
      // else if there are any warnings, display warning icon in row
      // else if any informational messages, display info icon in row
      const errors = payline.Errors.filter(x => x.WarningLevel === WarningLevel.Critical);
      const warnings = payline.Errors.filter(x => x.WarningLevel === WarningLevel.Warning);
      const info = payline.Errors.filter(x => x.WarningLevel === WarningLevel.Informational);
      if (errors.length) {
        row.Error = true;
        row.Message = errors[0].Message;
        row.ErrorsAndWarningsSort = 1;
      } else if (warnings.length) {
        row.Warning = true;
        row.Message = warnings[0].Message;
        row.ErrorsAndWarningsSort = 2;
      } else if (info.length) {
        row.Info = true;
        row.Message = info[0].Message;
        row.ErrorsAndWarningsSort = 3;
      }
    }
  }

  private _startEditingCell(rowIndex: number, column: string | Column<any>) {
    this.gridApi.startEditingCell({
      rowIndex: rowIndex,
      colKey: column
    });
  }

  private _updateColVizPref(colName: string, checked: boolean) {
    const visibility = this.userPrefSvc.getPayrollColumnVisibility(
      this.clientCode,
      this.userPreferences
    );

    const v = { Key: colName, Value: checked };

    if (!visibility) {
      this.userPreferences = this.userPrefSvc.setCompanyKey(this.clientCode, this.userPreferences);
      this.userPreferences.ClientPrefs[this.clientCode].Payroll.ColumnVisibility = [v];
    } else {
      const column = visibility.find(x => x.Key == colName);

      if (column) {
        column.Value = checked;
      } else {
        visibility.push(v);
      }

      this.userPreferences.ClientPrefs[this.clientCode].Payroll.ColumnVisibility = visibility;
    }

    this._setColVizRequest();
  }

  private _updateRowTrackerAndCalculate(rowData: any, prevRow: any, colId: string) {
    this.currentRow = rowData;

    // bug #27892 - calculate when user navigates to a row of a different employee
    // if previous row needs to be calculated, issue the get-totals request then
    // write the totals to our payroll model and save the changes
    if (
      prevRow &&
      prevRow.data.PersonId != rowData.PersonId &&
      this.paysToCalc.includes(prevRow.data.PersonId) &&
      !prevRow.data.IsLoading
    ) {
      this._initiatePaylineCalculation(prevRow.data);
    }

    this.currentColId = colId;
  }

  // update our global OkToPayCount counter with; the counter governs
  // the enabled state of the go-to-preview button
  private _updateOkToPayCount() {
    // controls the disabled flag on review & submit button
    this.okToPayCount = this.payroll.EmployeePays.flatMap(pay =>
      pay.Paylines.filter(line => line.OkToPay)
    ).length;
  }

  // translate payline data back to the row model
  // for use on the payline detail editor
  private _updateRow(payline: Payline, rowData, employeePay: EmployeePay) {
    // toggle OKToPay
    rowData.OkToPay = payline.OkToPay;

    if (payline.PayFrequencyTaxOverride) {
      rowData.Frequency = PayrollFrequency[payline.PayFrequencyTaxOverride];
    } else {
      rowData.Frequency = PayrollFrequency[employeePay.PayFrequency];
    }

    // populate earning and deduction row data from payline edits
    this._buildGridEarningData(rowData, payline, employeePay);
    this._buildGridDeductionData(rowData, payline, employeePay);
  }

  // after a row is updated in the grid, apply the changes to our underlying data model
  private _updatePayline(payline: Payline, rowData, setToCalc: boolean = false) {
    // set OkToPay
    payline.OkToPay = rowData.OkToPay;

    // step thru the earnings on the client and match them to the earning within
    // the payline; then find the corresponding hour and rate cells in the row
    // data and apply the row data to our underlying employee earning
    Object.keys(this.earningConfigs).forEach(earningId => {
      const earnCfg = this.earningConfigs[earningId];
      let earning = payline.Earnings.find(e => e.EarningId == earningId);
      if (earning) {
        // check to see if rowData column exists first
        // earnings that do not display in grid can be edited in the detail view
        if (Object.keys(rowData).includes(`${earnCfg.Name}Hours`)) {
          earning.Hours = rowData[`${earnCfg.Name}Hours`];
        }

        if (Object.keys(rowData).includes(`${earnCfg.Name}Rate`)) {
          earning.TemporaryRateAmount = rowData[`${earnCfg.Name}Rate`];
        }
      }
    });

    // step thru all the deductions on the client and match them to deductions
    // within the payline.  then find corresponding cells in our row data and
    // apply the cell data to the employee's deductions
    Object.keys(this.deductionConfigs).forEach(deductionId => {
      const dedCfg = this.deductionConfigs[deductionId];
      let deduction = payline.Deductions.find(d => d.DeductionCode == deductionId);
      // check to see if rowData column exists first
      // earnings that do not display in grid can be edited in the detail view
      if (deduction && Object.keys(rowData).includes(`${dedCfg.Name}`)) {
        if (dedCfg.CalculationMethod === DeductionCalculations.Fixed) {
          deduction.TemporaryAmount = rowData[`${dedCfg.Name}`];
        } else if (
          dedCfg.CalculationMethod === DeductionCalculations.Rate ||
          dedCfg.IsPercentageDeduction
        ) {
          deduction.TemporaryRate = rowData[`${dedCfg.Name}`];
        }
      }
    });

    let rateData = rowData.RateData as RowRateData;
    if (rateData.Type == RateType.Reg || rateData.Type == RateType.Salary) {
      var earningId = rateData.RegId || rateData.SalaryId;
      var earning = payline.Earnings.find(e => e.EarningId == earningId);
      if (earning) {
        earning.TemporaryRateAmount = rowData.Rate;
      }
    }

    // update cached payroll object in the mgmt service
    this.payrollMgmt.updatePayline(
      this.clientCode,
      this.year,
      this.seqNo,
      rowData.PersonId,
      payline,
      setToCalc
    );
  }

  // responsible for updating the error icons that appear on the grid,
  // resetting the row's IsLoading flag and redrawing the row on the grid
  private _updateRowErrors(personId: number, paylines: Payline[]) {
    // task 9476 - loop through the payline collection we received from the base
    // fetch the associated row and update with error info, if any...
    paylines.forEach(payline => {
      let row = this.rowData.find(r => r.PaylineId == payline.PaylineId && r.PersonId == personId);

      // do not display error/warning/icon message
      // when ok to pay is unchecked
      if (!row.OkToPay) {
        row.IsLoading = false;

        this._refreshCells(row);

        if (row.PaylineType == PaylineType.Manual) {
          this._setManualPayInfoMessage(row);
        }
        return;
      }

      // reset errors/warnings/info messages & sort order
      row.Error = row.Warning = row.Info = false;
      row.Message = '';
      row.ErrorsAndWarningsSort = 0;

      this._setRowErrors(payline, row);

      row.IsLoading = false;

      this._refreshCells(row);
    });
  }

  private _validatePayroll(payroll: MasterPayrollObject) {
    // manual payrolls have their own page and should not be surfaced here.
    // note that manual check paylines can be added to a regular payroll,
    // but the type of the payroll should never be 'Manual' here
    if (this.payroll.PayType == PayTypes.Manual) {
      this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
    }

    if (!this.payrollMgmt.canUserAccessPayroll(payroll)) {
      this.router.navigate([`/client/${this.clientCode}/payroll`]);
    }

    // bug 6956
    if (!payroll.EmployeePays || !payroll.EmployeePays.length) {
      this.modalService.showAlertModal(
        'WARNING: There are no active employees for this company. To pay a non-active person, click on the Add Person link at the top of the pay grid.',
        ModalStates.Warning
      );
      return;
    }
  }

  private _formatRegRateColumn(value) {
    return `${this.decimalPipe.transform(value, '1.2-2')} /hr`;
  }

  private _formatSalaryRateColumn(value) {
    return `$${this.decimalPipe.transform(value, '1.2-2')}`;
  }

  private _rateColumnIsButton(params: any) {
    if (params.data.RateData.Type == RateType.Both) {
      return true;
    }

    if (params.data.RateData.Type == RateType.None) {
      var rateData = params.data.RateData as RowRateData;
      let empPay = this.payroll.EmployeePays.find(p => p.PersonId == params.data.PersonId);
      let payline = empPay.Paylines.find(l => l.PaylineId == params.data.PaylineId);

      var regEarning = payline.Earnings.find(e => e.EarningId == rateData.RegId);
      var salarayEarning = payline.Earnings.find(e => e.EarningId == rateData.SalaryId);
      if (
        (regEarning?.TemporaryRateAmount > 0 || regEarning?.CurrentAmount > 0) !=
        (salarayEarning?.TemporaryRateAmount > 0 || salarayEarning?.CurrentAmount > 0)
      ) {
        // This is a weird looking XOR, if both fields are empty, we display 'Add' and want the class included
        // if both fields are filled we display 'Both' and want the class included, if one of the fields is filled
        // we are displaying the value, and do not want the class included

        // One rate value, don't include class
        return false;
      } else {
        // None or both rate values, include class
        return true;
      }
    }

    return false;
  }

  private _rateColumnIsActiveButton(params: any) {
    if (params.data.IsLoading || params.data.DetailEdited) {
      return false;
    }

    if (!this._rateColumnIsAllowedPaylineType(params)) {
      return false;
    }

    var isButton = this._rateColumnIsButton(params);
    if (!isButton) {
      return false;
    }

    let empPay = this.payroll.EmployeePays.find(p => p.PersonId == params.data.PersonId);
    let payline = empPay.Paylines.find(l => l.PaylineId == params.data.PaylineId);

    if (this.payrollMgmt.hasLaborAllocations(empPay, payline)) {
      return false;
    }

    return true;
  }

  private _rateColumnIsAllowedPaylineType(params) {
    const allowedPaylineTypes = [PaylineType.Normal];
    return allowedPaylineTypes.includes(params.data.PaylineType);
  }

  private _rateColumnGetRate(params: any) {
    var rateData = params.data.RateData as RowRateData;

    if (rateData.Type == RateType.Both) {
      return { IsBoth: true, Rate: null };
    }

    let empPay = this.payroll.EmployeePays.find(p => p.PersonId == params.data.PersonId);
    let payline = empPay.Paylines.find(l => l.PaylineId == params.data.PaylineId);

    if (!payline) {
      // this occurs when editing a person's details in payroll
      // which results in added paylines being deleted
      return { IsBoth: false, Rate: params.data.Rate };
    }

    var regEarning = payline.Earnings.find(e => e.EarningId == rateData.RegId);
    var salarayEarning = payline.Earnings.find(e => e.EarningId == rateData.SalaryId);

    if (this.payrollMgmt.hasLaborAllocations(empPay, payline)) {
      if (regEarning?.TemporaryRateAmount > 0 && salarayEarning?.TemporaryRateAmount > 0) {
        return { IsBoth: true, Rate: null };
      } else if (regEarning?.TemporaryRateAmount > 0 || regEarning?.CurrentAmount > 0) {
        return {
          IsBoth: false,
          Rate: this.laborAllocService.CalculateTotalRate(regEarning, payline)
        };
      } else if (salarayEarning?.TemporaryRateAmount > 0 || salarayEarning?.CurrentAmount > 0) {
        return {
          IsBoth: false,
          Rate: this.laborAllocService.CalculateTotalRate(salarayEarning, payline)
        };
      } else {
        return { IsBoth: false, Rate: null };
      }
    } else {
      return { IsBoth: false, Rate: params.data.Rate };
    }
  }

  private _setColumnVizIndicator() {
    let total = 0;
    let hidden = 0;
    this.gridApi.getColumnDefs().forEach((col: any) => {
      if (col?.children?.length) {
        col?.children.forEach(child => {
          total++;
          if (child.hide) {
            hidden++;
          }
        });
      } else {
        total++;
        if (col.hide) {
          hidden++;
        }
      }
    });

    this.columnVizIndicator = `Displaying ${total - hidden} of ${total} total columns`;
  }

  // #endregion
}

interface ProcessUnpinnedColumnsParams<TData = any, TContext = any> {
  columns: Column[];
  viewportWidth: number;
  // The grid api.
  api: GridApi<TData>;
  // Application context as set on `gridOptions.context`.
  context: TContext;
}
